import {
  gsap,
  ScrollTrigger,
  SplitText,
  U,
  EASE_IO,
  EASE_OUT,
  EASE_IN,
} from "@/lib/motion/gsap";
import { dirMul } from "@/lib/motion/dir";
import {
  coverScale,
  workBgClip,
  clipTo,
  placeThumb,
  canPlay,
  canReverse,
} from "@/lib/motion/geom";
import { et, killTrigger } from "@/lib/motion/triggers";
import type { SiteMotion } from "@/lib/motion/site-runtime";

/** Desktop Contact reveal — thin centered line → small center peek → full. */
const CONTACT_CLIP_LINE_DESKTOP = "polygon(30% 50%, 70% 50%, 70% 50%, 30% 50%)";
const CONTACT_CLIP_PEEK_DESKTOP = "polygon(30% 30%, 70% 30%, 70% 70%, 30% 70%)";
/** Mobile: open below Story map/content (founder), not over the map center. */
/*
 * Mobile Contact cover originates in the seam between Story title and map,
 * then peeks upward (not below the map — ADR-024 lower-origin reversed).
 */
const CONTACT_CLIP_LINE_MOBILE = "polygon(14% 44%, 86% 44%, 86% 44%, 14% 44%)";
const CONTACT_CLIP_PEEK_MOBILE = "polygon(10% 18%, 90% 18%, 90% 52%, 10% 52%)";
const CONTACT_CLIP_FULL = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";

/** Products → Services dismissal — slightly faster than the 2.5s reveal, still readable. */
const PRODUCTS_REVERSE_SCALE = 1.55;
/** Mosaic cover-scale under closing Products wrap (parallax reveal into Services). */
const PRODUCTS_REVERSE_MOSAIC_FROM = 1.62;
const PRODUCTS_REVERSE_MOSAIC_TO = 1.08;

function contactClipLine() {
  return window.innerWidth <= 600
    ? CONTACT_CLIP_LINE_MOBILE
    : CONTACT_CLIP_LINE_DESKTOP;
}

function contactClipPeek() {
  return window.innerWidth <= 600
    ? CONTACT_CLIP_PEEK_MOBILE
    : CONTACT_CLIP_PEEK_DESKTOP;
}

function q<T extends Element>(sel: string) {
  return gsap.utils.toArray<T>(sel);
}

function id(name: string) {
  return document.getElementById(name);
}

export function buildSpine(m: SiteMotion) {
  const heroEls = q<HTMLElement>("._hero_ele");
  const workEls = q<HTMLElement>("._work_ele");
  const workThumbs = q<HTMLElement>("#work .work_thumb");
  const fkInners = q<HTMLElement>(".fk_thumb_inner");
  const fkThumbs = q<HTMLElement>(".fk_thumb");
  const fakeWork = q<HTMLElement>(".fake_work_thumb_scaled");
  const heroH2 = q<HTMLElement>(".hero_head .hero_title");
  const workBgs = q<HTMLElement>(".work_bg");
  const workWrap = document.querySelector<HTMLElement>(".work_wrap");
  const workGridH = document.querySelector<HTMLElement>(".work_grid_h");
  const aboutHeads = q<HTMLElement>(".about_head");
  const lineShapes = q<HTMLElement>(".line_shape");
  const progressLines = q<HTMLElement>(".progress_line");

  /** scroll-spine `H()`: scale line_shape to fill progress_line column. */
  function lineShapeScale() {
    const shape = lineShapes[0];
    const column = progressLines[0];
    if (!shape || !column) return { scaleX: 1, scaleY: 1 };
    return {
      scaleX: column.clientWidth / shape.offsetWidth,
      scaleY: column.clientHeight / shape.offsetHeight,
    };
  }

  const thumbA = id("thumbA");
  const thumbB = id("thumbB");
  const thumbC = id("thumbC");
  const thumbD = id("thumbD");
  const hero = id("hero");
  const work = id("work");

  function setStoryScrubActive(active: boolean) {
    ScrollTrigger.getById("storyTrigger")?.[active ? "enable" : "disable"](
      false,
    );
  }

  /** Scrub-end pose for storyTrigger — contactTL starts from here. */
  function primeContactHandoff() {
    gsap.set(".contact_cover", {
      autoAlpha: 1,
      clipPath: contactClipPeek(),
      overwrite: "auto",
    });
    gsap.set(".contact_cover > i", { scale: 0.6, blur: 0, overwrite: "auto" });
    if (aboutHeads[0]) {
      gsap.set(aboutHeads[0], {
        y: -0.25 * window.innerHeight,
        autoAlpha: 0.3,
        blur: 10,
        overwrite: "auto",
      });
    }
    if (aboutHeads[1]) {
      gsap.set(aboutHeads[1], {
        y: 0.25 * window.innerHeight,
        autoAlpha: 0.3,
        blur: 10,
        overwrite: "auto",
      });
    }
  }

  function restoreContactPeekFromStory() {
    gsap.set(".contact_cover", {
      autoAlpha: 1,
      clipPath: contactClipPeek(),
      overwrite: "auto",
    });
    gsap.set(".contact_cover > i", { scale: 0.6, blur: 0, overwrite: "auto" });
  }

  /**
   * Fast flicks can reach story/contact before productsTL finishes.
   * Settle or hard-hide so Property Central cannot ghost over later stages.
   */
  function settleProductsTimeline() {
    if (m.productsTl?.isActive()) {
      m.productsTl.pause();
      m.productsTl.progress(1, true);
    }
  }

  function hideProductsPlane() {
    settleProductsTimeline();
    /*
     * Soft hide only — do NOT killTweensOf scrub targets (#productA/B,
     * marks, selector). killTweensOf removes those tweens from
     * productsTrigger permanently and freezes Atlas→Orbit until rebuild.
     */
    gsap.killTweensOf(".products_wrap");
    gsap.set(["#products", "#productA", "#productB"], {
      autoAlpha: 0,
      overwrite: "auto",
    });
    gsap.set(".products_wrap", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
      overwrite: "auto",
    });
    setSideNavMode("none", true);
    m.playProductVideos("pause");
  }

  function snapScrub(id: string, progress: number) {
    const anim = ScrollTrigger.getById(id)?.animation as
      | gsap.core.Timeline
      | undefined;
    if (!anim) return;
    anim.progress(progress);
    /* progress(p) is a no-op when already at p — force paint after hard sets. */
    anim.render(anim.time(), false, true);
  }

  /**
   * Kill only root-level tweens of `targets`.
   *
   * `gsap.killTweensOf` also removes children of paused timelines, so calling
   * it on servicesTL / productsTL targets deletes those members for good — the
   * morph silently stops running and the frame shows body black instead.
   */
  function killStrayTweensOf(targets: gsap.TweenTarget) {
    for (const tween of gsap.getTweensOf(targets)) {
      if (tween.parent && tween.parent !== gsap.globalTimeline) continue;
      tween.kill();
    }
  }

  /** Kill leftover services list/mosaic so later sections cannot stack over it. */
  function hideServicesPlane() {
    killStrayTweensOf([
      "#services",
      "#services-mosaic",
      "#services-mosaic .services_mosaic_plane",
      "#services-mosaic .services_mosaic_scrim",
      ".services_row",
    ]);
    gsap.set("#services", { autoAlpha: 0, y: 0, clearProps: "zIndex" });
    gsap.set("#services-mosaic", {
      autoAlpha: 0,
      clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
    });
    gsap.set("#services-mosaic .services_mosaic_scrim", { autoAlpha: 0 });
    gsap.set(".services_row", { y: 0, autoAlpha: 1 });
    setServicesMarqueeBackdrop(false, true);
  }

  /** Authoritative section id for CSS kill-switches (independent fixed layers). */
  function markSpineSection(section: string) {
    const prev = m.body.dataset.spineSection;
    m.body.dataset.spineSection = section;
    /*
     * One gesture → one *section* advance. Only spend when the id changes —
     * Work alone re-marks "work" on every case/scrub milestone; treating those
     * as spends made mobile feel like 1px-at-a-time (swipe dies after the
     * first mark). Boot/settleJump must not spend either (gestureLive false).
     */
    if (
      section !== prev &&
      m.gestureLive &&
      !m.seekMode &&
      !m.resizing
    ) {
      m.lastAdvanceGesture = m.gestureId;
    }
  }

  /**
   * Independent fixed layers are NOT children of `.landing_content`.
   * Parent autoAlpha never hides FakeThumbs / THREE / marquee / mosaic / cover.
   */
  function clearGlobalWorkLayers() {
    clearWorkPlanesForServices();
    clearWorkRailForServices();
    gsap.set(fkThumbs, { autoAlpha: 0, overwrite: "auto" });
    gsap.set([".work_bgs", ".work_marquee", ".work_bgs canvas", ".case4_bridge"], {
      autoAlpha: 0,
      scale: 1,
      blur: 0,
      overwrite: "auto",
    });
    gsap.set(".work_bgs .overlay", { autoAlpha: 0, overwrite: "auto" });
    setCase4Bridge("off", true);
    setWorkMarquee(false, true);
    m.slider?.pause();
  }

  /** Boot / jump land on Hero — full destination paint (was previously a no-op). */
  function settleHero() {
    m.heroPlayed = false;
    m.heroA?.pause();
    m.heroA?.progress(0);
    m.heroB?.pause();
    m.heroB?.progress(0);
    m.contactTl?.progress(0);
    m.storyTl?.progress(0);
    hideProjectsCopy();
    hideServicesPlane();
    hideProductsPlane();
    clearGlobalWorkLayers();
    snapScrub("servicesTrigger", 0);
    snapScrub("workTrigger", 0);
    snapScrub("cases-trigger", 0);
    snapScrub("productsTrigger", 0);
    snapScrub("storyTrigger", 0);
    if (workWrap) gsap.set(workWrap, { y: 0 });

    gsap.set("#hero", { autoAlpha: 1, pointerEvents: "auto", overwrite: "auto" });
    gsap.set("._hero_ele", { blur: 0, autoAlpha: 1, clearProps: "filter" });
    gsap.set(heroH2, { x: 0, clearProps: "transform" });
    gsap.set(
      ["#work", "#projects", "#services", "#products", "#about", "#contact"],
      { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
    );
    gsap.set(".contact_cover", {
      autoAlpha: 0,
      clipPath: contactClipLine(),
      overwrite: "auto",
    });
    gsap.set(".jump_menu_wrap", { autoAlpha: 0, overwrite: "auto" });
    setSideNavMode("none", true);
    m.body.classList.remove("work-scroll");
    m.body.classList.remove("contact-active");
    document.getElementById("contact")?.classList.remove("is-active");
    gsap.set(m.body, {
      background: "#080808",
      color: "#FFF",
      overwrite: "auto",
    });
    m.caseIndex = 1;
    m.casePrev = 1;
    m.servicesArmed = false;
    m.setJumpIndex(0);
    snapSide(null);
    markSpineSection("hero");
  }

  /**
   * Jump / `?section=work` land: general Our Work intro (titles + FakeThumbs grid).
   * Never call `restoreWorkAfterServices` here — that paints the active case
   * (MKIT copy, tags, side thumbs) on top of the intro (catastrophic overlay).
   */
  function settleWorkIntro() {
    m.heroPlayed = true;
    m.heroA?.progress(1);
    /* Pause + park HeroB so a prior seek("end") cannot leave progress===1
     * (casesEnter early-returns on !canPlay and never re-arms the rail). */
    m.heroB?.pause();
    m.heroB?.progress(0);
    m.contactTl?.progress(0);
    m.storyTl?.progress(0);

    const workSt = ScrollTrigger.getById("workTrigger");
    const workScrub = workSt?.animation as gsap.core.Timeline | undefined;
    workScrub?.progress(0);
    if (workWrap) gsap.set(workWrap, { y: 0 });

    hideServicesPlane();
    hideProductsPlane();
    hideProjectsCopy();
    gsap.set(
      ["#hero", "#projects", "#services", "#products", "#about", "#contact"],
      { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
    );
    gsap.set("#work", { autoAlpha: 1, pointerEvents: "auto", overwrite: "auto" });
    gsap.set("#work ._work_ele", {
      blur: 0,
      autoAlpha: 1,
      clearProps: "filter",
    });
    gsap.set([".work_bgs", ".work_marquee"], {
      autoAlpha: 0,
      scale: 1,
      blur: 0,
      overwrite: true,
    });
    gsap.set(".work_bgs .overlay", { autoAlpha: 0 });
    setWorkMarquee(false, true);
    gsap.set(".contact_cover", {
      autoAlpha: 0,
      clipPath: contactClipLine(),
      overwrite: "auto",
    });
    gsap.set(".jump_menu_wrap", { autoAlpha: 1, overwrite: "auto" });

    if (m.caseIntroTimer) {
      clearTimeout(m.caseIntroTimer);
      m.caseIntroTimer = undefined;
    }

    /* Soft-hide only — no killTweensOf / overwrite (keeps HeroB fromTo kids). */
    gsap.set(["#side-nav-work", "#side-nav-work .side_nav_item"], {
      autoAlpha: 0,
    });
    document.getElementById("side-nav-work")?.classList.remove("is-active");
    setSideNavMode("none", true, { preserveTweens: true });

    gsap.set(fkThumbs, { autoAlpha: 1, overwrite: "auto" });
    snapThumbsToWork();
    releaseWorkScrub();
    ScrollTrigger.getById("cases-trigger")?.enable();

    m.body.classList.add("work-scroll");
    m.body.classList.remove("contact-active");
    document.getElementById("contact")?.classList.remove("is-active");
    gsap.set(m.body, {
      background: "#C7C7C7",
      color: "#121212",
      overwrite: "auto",
    });
    m.slider?.pause();
    m.caseIndex = 1;
    m.casePrev = 1;
    m.servicesArmed = false;
    m.setJumpIndex(1);
    snapSide("side-work");
    markSpineSection("work");
  }

  /**
   * Navbar / jump seeks skip intermediate `et()` and leave scrub mid-flight.
   * Hard-set the destination plane after the scroll chain — every independent
   * fixed layer must be asserted (parent hide ≠ descendant / sibling hide).
   */
  function settleJump(section: string) {
    /* Services keeps marquee as blurred backdrop; work jump lands on intro. */
    hideProjectsCopy({ keepMarquee: section === "services" });
    if (section === "hero") {
      hideServicesPlane();
      hideProductsPlane();
      m.servicesTl?.progress(0);
      m.productsTl?.progress(0);
      m.storyTl?.progress(0);
      m.contactTl?.progress(0);
      snapScrub("servicesTrigger", 0);
      settleHero();
      return;
    }
    if (section === "work") {
      hideServicesPlane();
      hideProductsPlane();
      m.servicesTl?.progress(0);
      m.productsTl?.progress(0);
      m.storyTl?.progress(0);
      m.contactTl?.progress(0);
      snapScrub("servicesTrigger", 0);
      settleWorkIntro();
      return;
    }
    if (section === "services") {
      hideProductsPlane();
      /* Jump seek skips scrub onEnter — hard-clear work rail + planes. */
      clearWorkPlanesForServices();
      clearWorkRailForServices();
      snapScrub("servicesTrigger", 1);
      m.servicesTl?.progress(1);
      m.productsTl?.progress(0);
      m.storyTl?.progress(0);
      m.contactTl?.progress(0);
      gsap.set(
        ["#hero", "#work", "#projects", "#products", "#about", "#contact"],
        { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
      );
      gsap.set("#services", {
        autoAlpha: 1,
        y: 0,
        zIndex: 200,
        pointerEvents: "auto",
        overwrite: "auto",
      });
      gsap.set("#services-mosaic", {
        autoAlpha: 1,
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      });
      gsap.set("#services-mosaic .services_mosaic_plane", {
        blur: 22,
        scale: 1.08,
      });
      gsap.set("#services-mosaic .services_mosaic_scrim", { autoAlpha: 0.72 });
      gsap.set(".services_row", { y: 0, autoAlpha: 1 });
      gsap.set(".contact_cover", {
        autoAlpha: 0,
        clipPath: contactClipLine(),
        overwrite: "auto",
      });
      gsap.set(".jump_menu_wrap", { autoAlpha: 1, overwrite: "auto" });
      setServicesMarqueeBackdrop(true, true);
      snapSide("side-service-1");
      m.lastServiceKey = "side-service-1";
      m.servicesArmed = true;
      m.body.classList.remove("work-scroll", "contact-active");
      document.getElementById("contact")?.classList.remove("is-active");
      m.setJumpIndex(2);
      markSpineSection("services");
      return;
    }
    if (section === "products") {
      hideServicesPlane();
      clearGlobalWorkLayers();
      snapScrub("servicesTrigger", 1);
      m.servicesTl?.progress(1);
      m.productsTl?.progress(1);
      m.storyTl?.progress(0);
      m.contactTl?.progress(0);
      gsap.set(
        ["#hero", "#work", "#projects", "#services", "#about", "#contact"],
        { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
      );
      gsap.set(["#products", "#productA"], {
        autoAlpha: 1,
        pointerEvents: "auto",
        overwrite: "auto",
      });
      gsap.set("#productB", { autoAlpha: 0, overwrite: "auto" });
      gsap.set(".products_wrap", {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        overwrite: "auto",
      });
      gsap.set(".contact_cover", {
        autoAlpha: 0,
        clipPath: contactClipLine(),
        overwrite: "auto",
      });
      gsap.set(".jump_menu_wrap", { autoAlpha: 1, overwrite: "auto" });
      setSideNavMode("products", true);
      snapSide(m.lastProductKey || "side-product-1");
      m.playProductVideos("play");
      m.body.classList.remove("work-scroll", "contact-active");
      document.getElementById("contact")?.classList.remove("is-active");
      m.setJumpIndex(3);
      markSpineSection("products");
      return;
    }
    /* story + contact — never leave productsTl.onComplete re-arming #side-nav-products */
    hideServicesPlane();
    clearGlobalWorkLayers();
    snapScrub("servicesTrigger", 1);
    m.servicesTl?.progress(1);
    settleProductsTimeline();
    m.productsTl?.progress(1);
    hideProductsPlane();
    m.storyTl?.progress(1);
    setSideNavMode("none", true);
    gsap.set("#side-nav-products", {
      autoAlpha: 0,
      pointerEvents: "none",
      overwrite: true,
    });
    gsap.set(".jump_menu_wrap", { autoAlpha: 1, overwrite: "auto" });
    snapSide(section === "contact" ? "side-contact" : "side-story");
    m.setJumpIndex(section === "contact" ? 5 : 4);
    if (section === "story" || section === "contact") {
      window.dispatchEvent(new Event("xattir:story-facts"));
    }
    if (section === "story") {
      m.contactTl?.progress(0);
      /* Story entry = storyTrigger start — never peek (that is scrub-end / contact handoff). */
      snapScrub("storyTrigger", 0);
      gsap.set(
        ["#hero", "#work", "#projects", "#services", "#products", "#contact"],
        { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
      );
      gsap.set("#about", {
        autoAlpha: 1,
        pointerEvents: "auto",
        overwrite: "auto",
      });
      if (aboutHeads.length) {
        gsap.set(aboutHeads, {
          y: 0,
          autoAlpha: 1,
          blur: 0,
          clearProps: "transform,filter",
        });
      }
      gsap.set(".contact_cover", {
        autoAlpha: 1,
        clipPath: contactClipLine(),
        pointerEvents: "none",
        overwrite: "auto",
      });
      gsap.set(".contact_cover > i", {
        scale: 1,
        blur: 0,
        overwrite: "auto",
      });
      m.body.classList.remove("work-scroll", "contact-active");
      document.getElementById("contact")?.classList.remove("is-active");
      markSpineSection("story");
      return;
    }
    if (section === "contact") {
      m.contactTl?.progress(1);
      /* Jump seek skips contactEnter — force form plane + kill leftover work/story paint. */
      gsap.set(
        ["#hero", "#work", "#projects", "#services", "#products", "#about"],
        { autoAlpha: 0, pointerEvents: "none", overwrite: "auto" },
      );
      gsap.set("#contact", {
        autoAlpha: 1,
        pointerEvents: "auto",
        overwrite: "auto",
      });
      gsap.set("#contact ._ele", { autoAlpha: 1, y: 0, overwrite: "auto" });
      gsap.set(".form_wrap", { autoAlpha: 1, overwrite: "auto" });
      gsap.set(".form_success", { autoAlpha: 0, overwrite: "auto" });
      gsap.set(".contact_cover", {
        autoAlpha: 1,
        clipPath: CONTACT_CLIP_FULL,
        pointerEvents: "none",
        overwrite: "auto",
      });
      gsap.set(".contact_cover > i", { scale: 1, blur: 30, overwrite: "auto" });
      document.getElementById("contact")?.classList.add("is-active");
      m.body.classList.add("contact-active");
      m.body.classList.remove("work-scroll");
      markSpineSection("contact");
    }
  }

  function resetCases() {
    m.caseTl?.kill();
    m.caseTl = null;
    gsap.set('[id^="case-"] .static > .SplitClass', { clearProps: "all" });
    gsap.set('[id^="case-"] .tag', { clearProps: "all" });
    gsap.set('[id^="case-"]', { clearProps: "opacity,visibility" });
    gsap.set('[id^="case-"]', { autoAlpha: 0 });
    gsap.set("#case-1", { autoAlpha: 1 });
    m.caseIndex = 1;
    m.casePrev = 1;
  }

  function splitLines(selector: string) {
    q<HTMLElement>(selector).forEach((el) => {
      const prev = (el as HTMLElement & { _splitInstance?: SplitText })
        ._splitInstance;
      prev?.revert();
      const split = new SplitText(el, {
        type: "lines",
        linesClass: "_splitLines",
      });
      split.lines.forEach((line) => {
        const span = document.createElement("span");
        span.classList.add("SplitClass");
        while (line.firstChild) span.appendChild(line.firstChild);
        line.appendChild(span);
      });
      (el as HTMLElement & { _splitInstance?: SplitText })._splitInstance =
        split;
    });
  }

  function rebuildSplits() {
    splitLines("._split");
    m.projectsSplit?.revert();
    const projectsStatic = document.querySelector("#projects .static");
    if (projectsStatic) {
      m.projectsSplit = new SplitText("#projects .static", {
        type: "lines",
        linesClass: "SplitClass",
      });
    }
  }

  function reset() {
    m.thumbGap = 0.003 * window.innerWidth;
    if (m.smoother) gsap.set(m.smoother, { scrollTop: 0 });
    window.scroll(0, 0);
    [
      "progressTrigger",
      "workTrigger",
      "cases-trigger",
      "servicesTrigger",
      "storyTrigger",
      "productsTrigger",
    ].forEach(killTrigger);
    m.sideTl?.kill();
    m.heroA?.kill();
    m.heroB?.kill();
    m.caseIntro?.kill();
    m.servicesTl?.kill();
    m.productsTl?.kill();
    m.storyTl?.kill();
    m.contactTl?.kill();
    m.caseTl?.kill();
    if (m.caseIntroTimer) clearTimeout(m.caseIntroTimer);
    gsap.killTweensOf(
      [
        fkThumbs,
        heroH2,
        heroEls,
        workEls,
        fakeWork,
        lineShapes,
        thumbA,
        thumbB,
        thumbC,
        thumbD,
      ].filter(Boolean),
    );
    splitLines("._split");
    resetCases();
    setService(0);
    m.heroPlayed = false;
    /* Fresh rebuild is Hero/Work — do not leave servicesArmed stuck true or
     * heroB onComplete will skip arming Our Work thumbs after settleWorkIntro. */
    m.servicesArmed = false;
    m.serviceIndex = 0;
    m.lastServiceKey = "side-service-1";
    m.sideKey = null;
    m.sidePrev = null;
    m.body.classList.remove("work-scroll");
    m.body.classList.remove("contact-active");
    document.getElementById("contact")?.classList.remove("is-active");
    m.slider?.goToSlide(0, 0.1);
    if (fakeWork[0] && workThumbs[0]) {
      gsap.set(fakeWork[0], {
        y: 0,
        scale: () => coverScale(workThumbs[0], fakeWork[0], 0.4),
      });
    }
    fakeWork.slice(1).forEach((el) => {
      if (workThumbs[0])
        gsap.set(el, { y: 0, scale: () => coverScale(workThumbs[0], el, 0) });
    });
    gsap.set(fkThumbs, {
      clearProps: "clipPath,transform,x,y,opacity,visibility",
    });
    gsap.set(fkThumbs, { x: 0, y: 0, autoAlpha: 1, overwrite: true });
    gsap.set(heroH2, { clearProps: "transform,x,y,filter" });
    gsap.set(heroH2, { x: 0 });
    gsap.set(heroEls, { clearProps: "filter" });
    gsap.set(heroEls, { blur: 0 });
    gsap.set("[data-meteors-host]", { clearProps: "filter" });
    placeThumbs();
    gsap.set(m.body, { background: "#080808", color: "#FFF" });
    m.body.dataset.spineSection = "hero";
    gsap.set(
      [
        fakeWork,
        ".landing_content",
        ".work_bgs",
        ".work_marquee",
        "#services-mosaic",
        ".contact_cover",
        ".progress_line > i",
        ".jump_menu_wrap",
        ".side_container",
        ".side_nav",
      ],
      { autoAlpha: 0 },
    );
    setSideNavMode("none", true);
    gsap.set([".work_bgs", ".work_marquee"], {
      scale: 1,
      blur: 0,
      clearProps: "filter",
    });
    document.getElementById("services-mosaic")?.classList.remove("is-on");
    setWorkMarquee(false, true);
    gsap.set(".contact_cover", {
      clipPath: contactClipLine(),
      overwrite: "auto",
    });
    gsap.set(".contact_cover > i", { scale: 1, blur: 0 });
    if (aboutHeads.length) {
      gsap.set(aboutHeads, {
        y: 0,
        autoAlpha: 1,
        blur: 0,
        clearProps: "transform,filter",
      });
    }
    gsap.set([hero, ".product_video video"], {
      autoAlpha: 1,
    });
    /* Hero no longer hosts inline thumbs — keep fk thumbs hidden until work enter. */
    gsap.set(fkThumbs, { autoAlpha: 0 });
    gsap.set(fakeWork, { autoAlpha: 1 });
    gsap.set(lineShapes, {
      scaleX: 1,
      scaleY: 1,
      autoAlpha: 1,
      clearProps: "transform",
    });
    m.slider?.slideConfig.forEach((_, i) => {
      const h = workBgs[0]?.offsetHeight ?? 0;
      m.slider?.setSlideOffset(i, -(window.innerHeight - h) / 2);
    });
  }

  function setService(index: number) {
    m.serviceIndex = index;
    document.querySelectorAll(".services_row").forEach((row, i) => {
      row.classList.toggle("active", i === index);
    });
  }

  /**
   * Exactly one side-nav (or none). Hides the other + pe so ghost
   * `#side-nav-work` thumbs cannot scrollTo / steal clicks on Products.
   *
   * Child nodes that were animated with autoAlpha keep inline
   * `visibility:visible`, which punches through a parent autoAlpha:0 —
   * so hide/show must also paint items + marks (Atlas/Orbit ghosts).
   */
  /** Re-apply productsTrigger scrub pose (Atlas/Orbit marks + selector). */
  function syncProductsScrub() {
    const st = ScrollTrigger.getById("productsTrigger");
    const anim = st?.animation as gsap.core.Timeline | undefined;
    if (!st || !anim) return;
    anim.progress(st.progress);
    /* progress(p) is a no-op when already at p — force paint after hard sets. */
    anim.render(anim.time(), false, true);
  }

  /**
   * Pose Atlas/Orbit from live scroll when the scrub exists. Do not force
   * progress from lastProductKey — scrub:true re-renders at real scroll and
   * undoes hard snaps. Arriving from Story sits past end → Orbit; upward
   * scroll wipes back to Atlas on its own.
   */
  function poseProductsScrub() {
    const st = ScrollTrigger.getById("productsTrigger");
    if (st) {
      syncProductsScrub();
      m.lastProductKey =
        st.progress > 0.5 ? "side-product-2" : "side-product-1";
      return;
    }
    /* No-trigger fallback (scrub not yet built). */
    const orbit = m.lastProductKey === "side-product-2";
    if (orbit) {
      gsap.set("#productA", { autoAlpha: 0, overwrite: "auto" });
      gsap.set("#productB", {
        autoAlpha: 1,
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        overwrite: "auto",
      });
      gsap.set("#productB video", { autoAlpha: 1, overwrite: "auto" });
    } else {
      gsap.set("#productA", { autoAlpha: 1, overwrite: "auto" });
      gsap.set("#productB", {
        autoAlpha: 1,
        clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
        overwrite: "auto",
      });
      gsap.set("#productA video", { autoAlpha: 1, overwrite: "auto" });
      gsap.set("#productB video", { autoAlpha: 0, overwrite: "auto" });
    }
  }

  /** Re-apply cases-trigger scrub pose (work side-nav selector y). */
  function syncCasesScrub() {
    const st = ScrollTrigger.getById("cases-trigger");
    const anim = st?.animation as gsap.core.Timeline | undefined;
    if (!st || !anim) return;
    anim.progress(st.progress);
    anim.render(anim.time(), false, true);
  }

  /** Atlas→Orbit scrub — recreate whenever prior overwrite/kill detached members. */
  function buildProductsScrub() {
    killTrigger("productsTrigger");
    /*
     * Seed Atlas visible before recording. If we rebuild while Story/hide left
     * #productA at autoAlpha 0, a scrub that only `.set`s it to 0 at the END
     * leaves progress 0 stuck hidden — first Products visit works (productsTl
     * paints A) but every revisit is a black plane with the rail still on.
     */
    gsap.set("#productA", { autoAlpha: 1, overwrite: "auto" });
    gsap.set("#productB", { autoAlpha: 1, overwrite: "auto" });
    gsap.set("#products video", { autoAlpha: 1, overwrite: "auto" });
    gsap
      .timeline({
        scrollTrigger: {
          id: "productsTrigger",
          trigger: "#productsTrigger",
          start: "10% 0%",
          end: "90% 0%",
          scrub: true,
          invalidateOnRefresh: true,
        },
        defaults: { ease: "none" },
      })
      .set("#productA", { autoAlpha: 1 }, 0)
      .set("#productB", { autoAlpha: 1 }, 0)
      .from("#productB video", { autoAlpha: 0, duration: 1 }, 0)
      .fromTo(
        "#productB",
        { clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)" },
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          duration: 2,
        },
        0,
      )
      .to("#productA .product_video", { scale: 1, duration: 2 }, 0)
      .fromTo(
        "#productB .product_video",
        { scale: 1.5 },
        { scale: 1, duration: 2 },
        0,
      )
      .fromTo(
        "#side-p-1 .product_side_mark",
        { autoAlpha: 1 },
        { autoAlpha: 0.2, duration: 0.25 },
        1,
      )
      .fromTo(
        "#side-p-2 .product_side_mark",
        { autoAlpha: 0.2 },
        { autoAlpha: 1, duration: 0.25 },
        1,
      )
      .fromTo(
        "#side-nav-products .side_nav_selector",
        { y: 0 },
        { y: "100%", duration: 2 },
        0,
      )
      .set("#productA", { autoAlpha: 0 });
  }

  /** Hard-paint settled Products (snap / story reverse fallback). */
  function paintProductsEntered() {
    gsap.killTweensOf(["#services", "#services-mosaic", ".services_row"]);
    gsap.set(["#services", "#services-mosaic"], {
      autoAlpha: 0,
      overwrite: "auto",
    });
    gsap.set("#services", { clearProps: "zIndex", overwrite: "auto" });
    gsap.set(".services_row", { autoAlpha: 0, overwrite: "auto" });
    clearWorkPlanesForServices();
    gsap.set(["#products", "#productA"], {
      autoAlpha: 1,
      overwrite: "auto",
    });
    gsap.set("#products video", { autoAlpha: 1, overwrite: "auto" });
    gsap.set(".products_wrap", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      overwrite: "auto",
    });
    gsap.killTweensOf(".products_wrap");
    gsap.set("#side-nav-products .side_nav_item", {
      y: 0,
      autoAlpha: 1,
      overwrite: "auto",
    });
    buildProductsScrub();
    setSideNavMode("products", true);
    poseProductsScrub();
    m.playProductVideos("play");
  }

  function setSideNavMode(
    mode: "work" | "products" | "none",
    immediate = false,
    opts?: { preserveTweens?: boolean },
  ) {
    const work = document.getElementById("side-nav-work");
    const products = document.getElementById("side-nav-products");
    const showWork = mode === "work";
    const showProducts = mode === "products";
    work?.classList.toggle("is-active", showWork);
    products?.classList.toggle("is-active", showProducts);
    const preserve = !!opts?.preserveTweens;

    const paint = (el: HTMLElement | null, on: boolean) => {
      if (!el) return;
      /*
       * Soft-show must not killTweensOf the rail — heroB / productsTl own
       * the fade-in fromTo. Killing mid-onStart snapped the parent and
       * could leave children at the from-state under CSS lift.
       * Hiding must also skip kill when preserveTweens — settleWorkIntro
       * parks a paused HeroB whose side-nav fromTo must survive for the
       * next casesEnter play (killTweensOf / overwrite removes those kids).
       */
      if (!on && !preserve) gsap.killTweensOf(el);
      if (immediate || m.seekMode) {
        gsap.set(el, {
          autoAlpha: on ? 1 : 0,
          pointerEvents: on ? "auto" : "none",
          ...(preserve ? {} : { overwrite: on ? "auto" : true }),
        });
        return;
      }
      gsap.to(el, {
        autoAlpha: on ? 1 : 0,
        pointerEvents: on ? "auto" : "none",
        duration: on ? 0.55 : 0.35,
        ease: on ? EASE_OUT : EASE_IN,
        ...(preserve ? {} : { overwrite: on ? "auto" : true }),
      });
    };

    paint(work, showWork);
    paint(products, showProducts);

    /*
     * Marks + selector are owned by productsTrigger scrub. Never
     * killTweensOf them — that permanently detaches scrub members and
     * freezes Atlas→Orbit. CSS `:not(.is-active)` already hard-hides
     * children when the rail is inactive.
     */
    if (!showProducts) {
      gsap.killTweensOf("#side-nav-products .side_nav_item");
      gsap.set("#side-nav-products .side_nav_item", {
        autoAlpha: 0,
        overwrite: "auto",
      });
    } else if (immediate || m.seekMode) {
      gsap.killTweensOf("#side-nav-products .side_nav_item");
      gsap.set("#side-nav-products .side_nav_item", {
        autoAlpha: 1,
        overwrite: "auto",
      });
      syncProductsScrub();
    }

    /*
     * Work selector y is owned by cases-trigger scrub — never clearProps /
     * overwrite:true on it (same trap as products marks). Soft-show thumbs
     * when arming; hard-hide item autoAlpha when disarming so jump/settle
     * cannot leave Our Work covers stacked over SUB-SERVICES (CSS
     * `:not(.is-active)` is the kill-switch, but inline ghosts still need
     * a soft set — never touch the scrub-owned selector).
     */
    if (!showWork) {
      gsap.set("#side-nav-work .side_nav_item", {
        autoAlpha: 0,
        ...(preserve ? {} : { overwrite: "auto" }),
      });
    } else if (immediate || m.seekMode) {
      gsap.set("#side-nav-work .side_nav_item", {
        autoAlpha: 1,
        overwrite: "auto",
      });
      syncCasesScrub();
    } else {
      syncCasesScrub();
    }
  }

  /** Services own the right rail — kill work thumbs + FakeThumb leftovers. */
  function clearWorkRailForServices() {
    document.getElementById("side-nav-work")?.classList.remove("is-active");
    gsap.killTweensOf(["#side-nav-work", "#side-nav-work .side_nav_item"]);
    gsap.set("#side-nav-work", {
      autoAlpha: 0,
      pointerEvents: "none",
      overwrite: true,
    });
    gsap.set("#side-nav-work .side_nav_item", {
      autoAlpha: 0,
      overwrite: "auto",
    });
    gsap.set(".fk_thumb", { autoAlpha: 0, overwrite: "auto" });
    setSideNavMode("none", true);
  }

  function swapSide(
    fromKey: string | null,
    toKey: string | null,
    dir: number,
    delay?: number,
    onDone?: () => void,
  ) {
    const done = () => onDone?.();
    if (m.resizing) {
      done();
      return;
    }
    if (toKey === m.sideKey && !m.sideTl?.isActive()) {
      done();
      return;
    }
    if (m.sideTl?.isActive()) {
      /*
       * progress(1)/kill fire onComplete/onInterrupt → done(). Clear
       * callbacks first or snapSide-as-onDone re-enters forever.
       */
      const active = m.sideTl;
      m.sideTl = null;
      active.eventCallback("onComplete", null);
      active.eventCallback("onInterrupt", null);
      active.progress(1);
      active.kill();
      m.sideKey = m.sidePrev;
    }
    const current = m.sideKey;
    if (current === toKey) {
      done();
      return;
    }
    const forward = dir === 1;
    const staggerDir = forward ? 1 : -1;
    const fromY = forward ? "100%" : "-100%";
    m.sidePrev = toKey;
    m.sideTl = gsap.timeline({
      delay,
      paused: true,
      onComplete() {
        m.sideKey = toKey;
        done();
      },
      onInterrupt() {
        done();
      },
    });
    if (fromKey) {
      m.sideTl.to(
        `.${fromKey}`,
        { autoAlpha: 0, duration: 0.5, ease: EASE_IN },
        0,
      );
    }
    m.sideTl
      .set(`.side_container:not(.${toKey})`, { autoAlpha: 0 })
      .set(`.${toKey}`, { autoAlpha: 1 });
    if (toKey) {
      /* Keep bottom CTAs (Contact us / Book a demo) stable — only swap mdl/top copy. */
      m.sideTl.set(
        `.${toKey} .side_btm, .${toKey} .side_btm ._ele`,
        { y: 0, autoAlpha: 1, clearProps: "transform" },
        0,
      );
      m.sideTl
        .fromTo(
          `.${toKey} .side_mdl .SplitClass, .${toKey} .side_top .SplitClass`,
          { y: fromY, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            stagger: 0.05 * staggerDir,
            ease: EASE_OUT,
            duration: 0.5,
          },
        )
        .fromTo(
          `.${toKey} .side_mdl ._ele, .${toKey} .side_top ._ele`,
          { y: fromY, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            stagger: 0.05 * staggerDir,
            ease: EASE_OUT,
            duration: 0.5,
          },
          "<",
        );
    }
    if (m.seekMode) m.sideTl.seek("end", false);
    else m.sideTl.play();
  }

  /** Hard-set left rail copy without animating (used by scroll-end snaps). */
  function snapSide(toKey: string | null) {
    const tl = m.sideTl;
    m.sideTl = null;
    if (tl) {
      /* Null callbacks before kill — onInterrupt→done→snapSide stacked infinitely. */
      tl.eventCallback("onComplete", null);
      tl.eventCallback("onInterrupt", null);
      tl.kill();
    }
    q(".side_container").forEach((el) =>
      gsap.set(el, { autoAlpha: 0, clearProps: "filter" }),
    );
    if (toKey) {
      /* Both rails — RTL mirrors SideRight to the visual left (story facts). */
      gsap.set([".side_left", ".side_right"], { autoAlpha: 1 });
      gsap.set(`.${toKey}`, { autoAlpha: 1 });
      /* Force every copy node visible — interrupted swapSide left ._ele at 0. */
      gsap.set(
        `.${toKey} .SplitClass, .${toKey} ._ele, .${toKey} ._split, .${toKey} ._splitLines, .${toKey} span, .${toKey} li`,
        {
          y: 0,
          autoAlpha: 1,
          blur: 0,
          clearProps: "filter,transform",
        },
      );
      /* Story/contact/services never share the rail with work thumbs / Atlas. */
      if (
        toKey === "side-story" ||
        toKey === "side-contact" ||
        toKey.startsWith("side-service")
      ) {
        setSideNavMode("none", true);
      }
    }
    m.sideKey = toKey;
    m.sidePrev = toKey;
  }

  function introCase(n: number) {
    m.caseIntro?.kill();
    m.caseTl?.kill();
    setWorkMarquee(n === 4, true);
    m.caseIntro = gsap
      .timeline({
        onComplete: () => clearCaseCopy(n),
      })
      .set([`#projects`, `#case-${n}`], { autoAlpha: 1, blur: 0, y: 0 }, 0)
      .set(
        `#case-${n} .project_title, #case-${n} .static, #case-${n} .static > .SplitClass`,
        { autoAlpha: 1, y: 0, blur: 0, clearProps: "filter,transform" },
        0,
      )
      .fromTo(
        `#case-${n} .static > .SplitClass`,
        { y: "100%", autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1,
          ease: EASE_OUT,
          duration: 1,
        },
        0,
      )
      .fromTo(
        `#case-${n} ._ele`,
        { y: "100%", autoAlpha: 0 },
        { y: 0, autoAlpha: 1, stagger: 0.1, ease: EASE_OUT, duration: 1 },
        0,
      );
  }

  function hideCase(n: number) {
    m.caseIntro?.kill();
    setWorkMarquee(false, true);
    m.caseIntro = gsap.timeline();
    m.caseIntro
      .to(`#case-${n}`, {
        autoAlpha: 0,
        blur: 10,
        duration: 0.5,
        ease: EASE_OUT,
      })
      .set("#projects", { autoAlpha: 0 });
  }

  /**
   * Kill residual GSAP `filter: blur()` on case titles/body.
   * Fast scroll often interrupts intro/swap mid-blur and leaves CareLend etc. soft.
   */
  function clearCaseCopy(n: number) {
    const sel = [
      `#case-${n}`,
      `#case-${n} .project_title`,
      `#case-${n} .static`,
      `#case-${n} .static > .SplitClass`,
      `#case-${n} ._ele`,
      `#case-${n} .tag`,
    ];
    killStrayTweensOf(sel);
    gsap.set(sel, {
      autoAlpha: 1,
      y: 0,
      blur: 0,
      clearProps: "filter,transform",
    });
    q(".project").forEach((el) =>
      el.classList.toggle("is-active-copy", el.id === `case-${n}`),
    );
  }

  /** Undo hideProjectsCopy — title, tags, and body visible for the active case. */
  function showCaseCopy(n: number) {
    setWorkMarquee(n === 4, true);
    q(".project").forEach((el) => {
      const on = el.id === `case-${n}`;
      el.classList.toggle("is-active-copy", on);
      el.classList.toggle("is-marquee-copy", on && n === 4);
      gsap.set(el, { autoAlpha: on ? 1 : 0, blur: on ? 0 : 10 });
    });
    gsap.set("#projects", { autoAlpha: 1 });
    const live = [
      `#case-${n}`,
      `#case-${n} .project_title`,
      `#case-${n} .static`,
      `#case-${n} .static > .SplitClass`,
      `#case-${n} ._ele`,
      `#case-${n} .tag`,
    ];
    killStrayTweensOf(live);
    gsap.set(live, {
      autoAlpha: 1,
      blur: 0,
      y: 0,
      clearProps: "filter,transform",
    });
  }

  /** Hard-hide case titles (esp. “And more.”) once past the work/cases zone. */
  function hideProjectsCopy(opts?: { keepMarquee?: boolean }) {
    m.caseIntro?.kill();
    m.caseTl?.kill();
    if (m.caseIntroTimer) {
      clearTimeout(m.caseIntroTimer);
      m.caseIntroTimer = undefined;
    }
    /* Services keeps the mosaic as blurred backdrop — don't kill it here. */
    if (!opts?.keepMarquee) setWorkMarquee(false, true);
    q(".project").forEach((el) => {
      el.classList.remove("is-active-copy", "is-marquee-copy", "is-on");
    });
    document.getElementById("projects")?.classList.remove("is-active");
    const copy = [
      "#projects",
      ".project",
      ".project .static",
      ".project .static > .SplitClass",
      ".project ._ele",
      ".project .tag",
    ];
    killStrayTweensOf(copy);
    gsap.set(copy, {
      autoAlpha: 0,
      blur: 0,
      clearProps: "filter",
    });
  }

  /**
   * Case-4 temporary melt plate (`work_cover_4_bridge`) between THREE and marquee.
   * Phases: off → covering (opaque over outgoing) → revealing (fade while dest paints).
   */
  type Case4BridgePhase = "off" | "covering" | "revealing";
  let case4BridgePhase: Case4BridgePhase = "off";

  function setCase4Bridge(phase: Case4BridgePhase, immediate = false) {
    const bridge = document.querySelector<HTMLElement>(".case4_bridge");
    if (!bridge) {
      case4BridgePhase = phase;
      return;
    }
    gsap.killTweensOf(bridge);
    case4BridgePhase = phase;
    if (immediate || m.seekMode || phase === "off") {
      gsap.set(bridge, {
        autoAlpha: phase === "covering" ? 1 : 0,
        overwrite: true,
      });
      return;
    }
    if (phase === "covering") {
      gsap.fromTo(
        bridge,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.45, ease: EASE_OUT, overwrite: true },
      );
      return;
    }
    /* revealing → fade out over painted marquee / mosaic */
    gsap.to(bridge, {
      autoAlpha: 0,
      duration: 0.7,
      ease: EASE_OUT,
      overwrite: true,
      onComplete: () => {
        if (case4BridgePhase === "revealing") case4BridgePhase = "off";
      },
    });
  }

  /** Case-4: full-bleed DOM marquee over THREE canvas (Aceternity pattern). */
  function setWorkMarquee(active: boolean, immediate = false) {
    const layer = document.querySelector<HTMLElement>(".work_marquee");
    const canvas = document.querySelector<HTMLElement>(".work_bgs canvas");
    const case4 = document.querySelector<HTMLElement>("#case-4");
    if (case4) case4.classList.toggle("is-marquee-copy", active);
    if (!active) layer?.classList.remove("is-services-bg");
    if (active) {
      clearCaseCopy(4);
      gsap.set(".project_set.work_mask", { blur: 0, clearProps: "filter" });
    }
    if (!layer) return;
    gsap.killTweensOf(layer);
    if (canvas) gsap.killTweensOf(canvas);
    if (immediate || m.seekMode) {
      gsap.set(layer, {
        autoAlpha: active ? 1 : 0,
        scale: 1,
        blur: 0,
      });
      if (!active) gsap.set(layer, { clearProps: "filter" });
      if (canvas) gsap.set(canvas, { autoAlpha: active ? 0 : 1 });
      return;
    }
    gsap.to(layer, {
      autoAlpha: active ? 1 : 0,
      scale: 1,
      blur: 0,
      duration: active ? 0.55 : 0.45,
      ease: EASE_OUT,
      overwrite: true,
    });
    if (canvas) {
      /* Splash still clears faster than marquee rises so And more doesn't linger. */
      gsap.to(canvas, {
        autoAlpha: active ? 0 : 1,
        duration: active ? 0.32 : 0.45,
        ease: EASE_OUT,
        overwrite: true,
      });
    }
  }

  /**
   * Case 3↔4: same THREE displace morph as MKIT↔Kenaz (plane 4 = multi-device
   * cover). After the morph settles, soft-crossfade the live DOM marquee over
   * the matching still. Reverse: hide marquee, morph THREE back to CareLend.
   * Do not flash a separate bridge plate — that read as an ugly hard cut.
   */
  function handoffCase4Marquee(forward: boolean) {
    if (m.seekMode) {
      setCase4Bridge("off", true);
      if (forward) {
        m.slider?.goToSlide(3, 0);
        setWorkMarquee(true, true);
        gsap.set(".work_bgs", { autoAlpha: 0, overwrite: true });
      } else {
        setWorkMarquee(false, true);
        m.slider?.goToSlide(2, 0);
        gsap.set(".work_bgs", { autoAlpha: 1, overwrite: true });
      }
      return;
    }
    if (forward) {
      setCase4Bridge("off", true);
      setWorkMarquee(false, true);
      gsap.set([".work_bgs", ".work_bgs canvas"], {
        autoAlpha: 1,
        overwrite: true,
      });
      /* Identical duration language to slide2/slide3 goToSlide(…, 1). */
      m.slider?.goToSlide(3, 1);
      /* Start marquee (and hide THREE splash) before morph fully settles. */
      window.setTimeout(() => {
        if (m.casePrev !== 4 && m.caseIndex !== 4) return;
        setWorkMarquee(true);
      }, 480);
      return;
    }
    setCase4Bridge("off", true);
    setWorkMarquee(false, true);
    gsap.set([".work_bgs", ".work_bgs canvas"], {
      autoAlpha: 1,
      overwrite: true,
    });
    m.slider?.goToSlide(2, 1);
  }

  /**
   * Services backdrop — flat image plane + contact-style whole-plane blur.
   * Final look: blur 36 / scale 1.1 / scrim on / full clip (ADR-015).
   * CSS defaults `.services_mosaic` to a collapsed clip-path — never
   * `clearProps: "clipPath"` when showing, or the wipe snaps shut again.
   */
  function setServicesMarqueeBackdrop(active: boolean, immediate = false) {
    const mosaic = document.getElementById("services-mosaic");
    if (!mosaic) return;
    const plane = mosaic.querySelector<HTMLElement>(".services_mosaic_plane");
    const scrim = mosaic.querySelector<HTMLElement>(".services_mosaic_scrim");
    killStrayTweensOf([mosaic, plane, scrim].filter(Boolean));

    if (active) gsap.set(".work_bgs", { autoAlpha: 0 });

    const mobile = window.innerWidth <= 600;
    const clipOpen = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const clipShut = "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)";

    if (immediate || m.seekMode) {
      const mosaicSet: gsap.TweenVars = {
        autoAlpha: active ? 1 : 0,
        overwrite: "auto",
      };
      /* Mobile: clip wipe is unstable ≤600px — force open/shut via autoAlpha only. */
      if (!mobile) {
        mosaicSet.clipPath = active ? clipOpen : clipShut;
      } else if (active) {
        mosaicSet.clipPath = clipOpen;
      }
      gsap.set(mosaic, mosaicSet);
      if (plane) {
        gsap.set(plane, {
          blur: active ? (mobile ? 18 : 22) : 0,
          scale: active ? 1.08 : 1,
          overwrite: "auto",
        });
      }
      if (scrim) {
        gsap.set(scrim, { autoAlpha: active ? 0.72 : 0, overwrite: "auto" });
      }
      return;
    }

    const dur = active ? 0.85 : 0.55;
    gsap.to(mosaic, {
      autoAlpha: active ? 1 : 0,
      clipPath: active ? clipOpen : clipShut,
      duration: dur,
      ease: active ? EASE_OUT : EASE_IN,
      overwrite: "auto",
    });
    if (plane) {
      gsap.to(plane, {
        blur: active ? 22 : 0,
        scale: active ? 1.08 : 1,
        duration: dur,
        ease: active ? EASE_OUT : EASE_IN,
        overwrite: "auto",
      });
    }
    if (scrim) {
      gsap.to(scrim, {
        autoAlpha: active ? 0.72 : 0,
        duration: dur * 0.85,
        delay: active ? 0.08 : 0,
        ease: active ? EASE_OUT : EASE_IN,
        overwrite: "auto",
      });
    }
  }

  /** Active work case from slide triggers (fallback: tracked caseIndex, min 1). */
  function resolveWorkCase(): number {
    const slideOrder = [
      { id: "slide4-in-in", caseNum: 4 },
      { id: "slide3-in-in", caseNum: 3 },
      { id: "slide2-in-in", caseNum: 2 },
    ] as const;
    for (const { id: stId, caseNum } of slideOrder) {
      const st = ScrollTrigger.getById(stId);
      if (st && st.progress > 0) return caseNum;
    }
    if (m.caseIndex >= 1 && m.caseIndex <= 4) return m.caseIndex;
    return 4;
  }

  /** Case-4 plane is DOM marquee — not a THREE cover UV morph source. */
  function isMarqueeWorkCase(caseNum = resolveWorkCase()) {
    return caseNum === 4;
  }

  /**
   * Kill competing marquee / THREE fades so scrub or enter can own opacity.
   * Image-morph paths assume a cover plane; marquee must crossfade instead.
   */
  function prepWorkServicesHandoff() {
    const layer = document.querySelector<HTMLElement>(".work_marquee");
    const canvas = document.querySelector<HTMLElement>(".work_bgs canvas");
    gsap.killTweensOf([".work_marquee", ".work_bgs", canvas].filter(Boolean));
    layer?.classList.remove("is-services-bg");
  }

  /** Services fully owns the plane — no residual marquee / work_bgs opacity. */
  function clearWorkPlanesForServices() {
    prepWorkServicesHandoff();
    setCase4Bridge("off", true);
    setWorkMarquee(false, true);
    gsap.set([".work_bgs", ".work_marquee", ".case4_bridge"], {
      autoAlpha: 0,
      scale: 1,
      blur: 0,
      overwrite: true,
    });
  }

  /**
   * Leave services → restore work plane for the real active case.
   * Paint Work FIRST, then hide mosaic — mosaic→0 before Work was the black flash.
   */
  function restoreWorkAfterServices() {
    const scrub = ScrollTrigger.getById("servicesTrigger")?.animation as
      | gsap.core.Timeline
      | undefined;
    scrub?.progress(0);

    const target = resolveWorkCase();
    const marqueeCase = isMarqueeWorkCase(target);
    const rows = q<HTMLElement>(".services_row");
    prepWorkServicesHandoff();
    killStrayTweensOf([
      "#services",
      "#services-mosaic",
      "#services-mosaic .services_mosaic_plane",
      "#services-mosaic .services_mosaic_scrim",
      rows,
      ".work_bgs",
      ".work_marquee",
    ]);
    /* Match buildServices idle — rows no longer use data-ycoord parking. */
    gsap.set(rows, { y: 32, autoAlpha: 0, overwrite: "auto" });
    gsap.set("#services", {
      autoAlpha: 0,
      y: 0,
      clearProps: "zIndex,filter",
      overwrite: "auto",
    });
    gsap.set(".work_mask", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      blur: 0,
      clearProps: "filter",
    });
    gsap.set([".work_bgs", ".work_marquee"], {
      scale: 1,
      blur: 0,
      y: 0,
      clearProps: "filter",
    });
    gsap.set(["#projects", "#case-4"], { y: 0, overwrite: "auto" });

    /* 1) Destination Work plane on before mosaic drops — no void frame. */
    paintWorkPlaneUnderServices(target, marqueeCase);

    /* 2) Then park Services / mosaic under the restored Work plane. */
    document.getElementById("services-mosaic")?.classList.remove("is-on");
    gsap.set("#services-mosaic", {
      autoAlpha: 0,
      clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
      overwrite: "auto",
    });
    gsap.set("#services-mosaic .services_mosaic_plane", {
      blur: 0,
      scale: 1,
      clearProps: "filter",
      overwrite: "auto",
    });
    gsap.set("#services-mosaic .services_mosaic_scrim", {
      autoAlpha: 0,
      overwrite: "auto",
    });
    setServicesMarqueeBackdrop(false, true);

    gsap.set("#side-nav-work", { autoAlpha: 1 });
    gsap.set("#side-nav-work .side_nav_item", {
      y: 0,
      autoAlpha: 1,
      clearProps: "transform",
    });
    setSideNavMode("work", true);
    showCaseCopy(target);
    gsap.set(".side_left", { autoAlpha: 1 });
    snapSide(`side-case-${target}`);

    m.caseIndex = target;
    m.casePrev = target;
    m.servicesArmed = false;
    m.serviceIndex = 0;
    setService(0);
    m.slider?.goToSlide(Math.max(0, target - 1), 0);
    m.setJumpIndex(1);
    markSpineSection("work");
    m.lock(false);
  }

  /**
   * Paint Work under an open Services mosaic without touching mosaic/services
   * timeline targets. Used before servicesTL.reverse() so the wipe shut
   * reveals Work instead of body #080808.
   */
  function paintWorkPlaneUnderServices(
    caseNum = resolveWorkCase(),
    marqueeCase = isMarqueeWorkCase(caseNum),
  ) {
    setCase4Bridge("off", true);
    gsap.set([".work_bgs", ".work_marquee"], {
      scale: 1,
      blur: 0,
      y: 0,
      clearProps: "filter",
    });
    if (marqueeCase) {
      gsap.set(".work_bgs", { autoAlpha: 0, overwrite: true });
      setWorkMarquee(true, true);
    } else {
      setWorkMarquee(false, true);
      gsap.set([".work_bgs", ".work_bgs canvas"], {
        autoAlpha: 1,
        overwrite: true,
      });
    }
  }

  /**
   * Settled Services splash under Products wrap — call BEFORE productsTl.reverse
   * so closing Products never reveals body black (mosaic was autoAlpha 0 at TL end).
   * `parallax` starts the plane zoomed so reverse can ease down to settled scale.
   */
  function paintServicesMosaicSettled(opts?: { parallax?: boolean }) {
    const mobile = window.innerWidth <= 600;
    const clipOpen = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const scale = opts?.parallax
      ? PRODUCTS_REVERSE_MOSAIC_FROM
      : PRODUCTS_REVERSE_MOSAIC_TO;
    gsap.set("#services-mosaic", {
      autoAlpha: 1,
      clipPath: clipOpen,
      overwrite: "auto",
    });
    gsap.set("#services-mosaic .services_mosaic_plane", {
      blur: mobile ? 18 : 22,
      scale,
      overwrite: "auto",
    });
    gsap.set("#services-mosaic .services_mosaic_scrim", {
      autoAlpha: 0.72,
      overwrite: "auto",
    });
  }

  function swapCase(from: number, to: number) {
    if (m.resizing) return;
    if (to === m.caseIndex && m.caseTl && !m.caseTl.isActive()) return;
    if (m.caseTl?.isActive()) {
      m.caseTl.progress(1);
      m.caseTl.kill();
      m.caseTl = null;
      m.caseIndex = m.casePrev;
    }
    const current = m.caseIndex;
    if (current === to) return;
    /*
     * Case swaps do not lock scroll (side rail only). On touch, still end the
     * fling so one flick cannot skip every Work case in one coast.
     */
    if (m.isTouch || window.innerWidth <= 600) {
      m.consumeTouchAdvance();
    }
    const forward = current < to;
    const staggerDir = forward ? 1 : -1;
    const fromY = forward ? "100%" : "-100%";
    m.casePrev = to;
    m.caseIntro?.kill();
    m.caseTl?.kill();
    /* Case 3↔4 marquee owned by handoffCase4Marquee (bridge plate). */
    if (to !== 4 && from !== 4) setWorkMarquee(false, true);
    m.caseTl = gsap.timeline({
      paused: true,
      onComplete() {
        m.caseIndex = to;
        clearCaseCopy(to);
      },
    });
    m.caseTl
      .fromTo(
        `#case-${from} .static > .SplitClass`,
        { autoAlpha: 1 },
        { autoAlpha: 0, ease: EASE_IN, duration: 0.5 },
        0,
      )
      .fromTo(
        `#case-${from} ._ele`,
        { autoAlpha: 1 },
        { autoAlpha: 0, ease: EASE_IN, duration: 0.5 },
        0,
      )
      .set(`.project:not(#case-${to})`, { autoAlpha: 0 })
      .set(`#case-${from}`, { autoAlpha: 0 })
      .set(`#case-${to}`, { autoAlpha: 1 })
      /* Titles: opacity/y only — no per-glyph blur (reads as soft/missing). */
      .fromTo(
        `#case-${to} .static > .SplitClass`,
        {
          y: fromY,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1 * staggerDir,
          ease: EASE_OUT,
          duration: 1,
        },
      )
      .fromTo(
        `#case-${to} ._ele`,
        { y: fromY, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1 * staggerDir,
          ease: EASE_OUT,
          duration: 1,
        },
        "<",
      );
    if (m.seekMode) m.caseTl.seek("end", false);
    else m.caseTl.play();
  }

  function buildHeroA() {
    placeThumbs();
    m.heroA = gsap.timeline({ id: "HeroA", paused: true });
    m.playFkVideos("play");
    m.heroA
      .fromTo(
        ".jump_menu_wrap",
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: U, ease: EASE_IO },
        0,
      )
      .fromTo(
        lineShapes,
        { scaleX: 1, scaleY: 1, autoAlpha: 1 },
        {
          scaleX: () => lineShapeScale().scaleX,
          scaleY: () => lineShapeScale().scaleY,
          autoAlpha: 0.2,
          duration: U,
          ease: EASE_IO,
        },
        0,
      )
      .fromTo(
        ".progress_line > i",
        { autoAlpha: 0 },
        { autoAlpha: 0.6, duration: 0.5, ease: EASE_IO },
        U,
      )
      .to(
        m.body,
        { background: "#C7C7C7", color: "#121212", duration: U, ease: EASE_IO },
        0,
      );
    if (thumbA && workThumbs[0]) {
      const holeA = fkInners[0] ?? workThumbs[0];
      m.heroA.to(
        thumbA,
        {
          clipPath: (_i, el) => clipTo(el as Element, holeA),
          duration: U,
          ease: EASE_IO,
          overwrite: "auto",
        },
        0,
      );
    }
    if (thumbB && workThumbs[2]) {
      const holeB = fkInners[2] ?? workThumbs[2];
      m.heroA.to(
        thumbB,
        {
          clipPath: (_i, el) => clipTo(el as Element, holeB),
          duration: U,
          ease: EASE_IO,
          overwrite: "auto",
        },
        0,
      );
    }
    m.heroA
      .fromTo(
        [thumbA, thumbB, thumbC, thumbD].filter(Boolean),
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: U / 2, ease: EASE_OUT },
        U / 2,
      );
    if (heroH2[0]) {
      m.heroA.fromTo(
        heroH2[0],
        { x: 0 },
        { x: `${10 * dirMul()}%`, duration: U / 2, ease: EASE_IN },
        0,
      );
    }
    if (heroH2[1]) {
      m.heroA.fromTo(
        heroH2[1],
        { x: 0 },
        { x: `${-10 * dirMul()}%`, duration: U / 2, ease: EASE_IN },
        0,
      );
    }
    m.heroA
      .fromTo(
        hero,
        { autoAlpha: 1 },
        { autoAlpha: 0, duration: U / 2, ease: EASE_IN },
        0,
      )
      .fromTo(
        heroEls,
        { blur: 0 },
        { blur: 10, duration: U / 2, ease: EASE_IN },
        0,
      )
      .fromTo(
        work,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: U / 2, ease: EASE_OUT },
        /* Overlap hero exit — starting at U/2 left a black beat. */
        U / 4,
      )
      .fromTo(
        workEls,
        { blur: 10, autoAlpha: 0 },
        { blur: 0, autoAlpha: 1, duration: U / 2, ease: EASE_OUT },
        U / 4,
      );
    m.heroA.eventCallback("onStart", () => {
      pauseWorkScrub();
    });
    m.heroA.eventCallback("onComplete", () => {
      snapThumbsToWork();
      releaseWorkScrub();
      m.lock(false);
    });
    m.heroA.eventCallback("onReverseComplete", () => {
      placeThumbs();
      gsap.set(fkThumbs, { autoAlpha: 0 });
      gsap.set([".work_bgs", ".work_marquee", ".case4_bridge"], {
        autoAlpha: 0,
        scale: 1,
        blur: 0,
      });
      setCase4Bridge("off", true);
      setWorkMarquee(false, true);
      m.playFkVideos("play");
      m.body.classList.remove("work-scroll");
      pauseWorkScrub();
      /* Marker only after reverse paints Hero — early "hero" CSS-kills work mid-fade. */
      markSpineSection("hero");
      m.lock(false);
    });
    const heroEnter = () => {
      if (!m.heroA || m.heroPlayed) return;
      if (!canPlay(m.heroA)) return;
      /*
       * Seek: park HeroA only — swapSide / work-scroll mid-chain left Work
       * painted under later settleJump destinations.
       */
      if (m.seekMode) {
        m.heroPlayed = true;
        m.heroA.seek("end", false);
        m.setJumpIndex(1);
        return;
      }
      m.heroPlayed = true;
      markSpineSection("work");
      pauseWorkScrub();
      m.body.classList.add("work-scroll");
      m.lock(true);
      if (window.innerWidth <= 600) m.heroA.timeScale(1.55).play();
      else m.heroA.timeScale(1).play();
      m.headerTl?.play();
      swapSide(null, "side-work", 1);
      m.playFkVideos("pause");
      m.setJumpIndex(1);
    };
    const heroReverse = () => {
      if (!m.heroA || !m.heroPlayed) return;
      if (!canReverse(m.heroA)) return;
      if (m.seekMode) {
        m.heroPlayed = false;
        m.heroA.seek(0, false);
        markSpineSection("hero");
        return;
      }
      prepareHeroReverse();
      /* Keep "work" until onReverseComplete so kill-switches do not blank the fade. */
      swapSide("side-work", null, -1);
      if (window.innerWidth > 600) m.headerTl?.reverse();
      m.heroPlayed = false;
      if (window.innerWidth <= 600) m.heroA.timeScale(1.7).reverse();
      else m.heroA.timeScale(1).reverse();
    };
    et("heroTrigger", "in", heroEnter);
    et("heroTrigger", "reverse", heroReverse);
  }

  function buildHeroB() {
    m.projectsSplit?.revert();
    m.projectsSplit = new SplitText("#projects .static", {
      type: "lines",
      linesClass: "SplitClass",
    });
    m.heroB = gsap.timeline({ id: "HeroB", paused: true });
    const d = 1.5 * U;
    [thumbA, thumbB, thumbC, thumbD].forEach((el) => {
      if (el)
        m.heroB!.to(
          el,
          { clipPath: () => workBgClip(), duration: d, ease: EASE_IO },
          0,
        );
    });
    m.heroB
      .to(
        fkThumbs,
        {
          x: 0,
          y: (_i, el) => {
            const i = fkThumbs.indexOf(el as HTMLElement);
            return (workBgs[0]?.offsetHeight ?? 0) * i;
          },
          duration: d,
          ease: EASE_IO,
        },
        0,
      )
      .to(
        fakeWork,
        {
          scale: 1,
          y: () => ((fakeWork[0]?.offsetHeight ?? 0) - window.innerHeight) / 2,
          duration: d,
          ease: EASE_IO,
        },
        0,
      )
      .call(() => {
        m.slider?.resume();
        m.slider?.showCurrent();
      })
      .set(m.body, { color: "#FFF" }, U)
      .set(".fk_thumb", { autoAlpha: 0 })
      .set(".work_bgs", { autoAlpha: 1 })
      .call(() => m.body.classList.remove("work-scroll"))
      .set(m.body, { background: "#080808" })
      .fromTo(
        ".work_bgs .overlay",
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 1, ease: EASE_OUT },
      )
      .fromTo(
        "#side-nav-work",
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 1,
          ease: EASE_OUT,
          /*
           * Arm .is-active only — full setSideNavMode(immediate) would
           * soft-snap thumbs and fight this stagger entrance (products
           * timeline toggles classes the same way).
           */
          onStart: () => {
            document.getElementById("side-nav-work")?.classList.add("is-active");
            document
              .getElementById("side-nav-products")
              ?.classList.remove("is-active");
          },
        },
        U,
      )
      .fromTo(
        "#side-nav-work .side_nav_item",
        { y: "100%", autoAlpha: 0 },
        { y: 0, autoAlpha: 1, stagger: 0.1, duration: 1, ease: EASE_OUT },
        U,
      );
    m.heroB.eventCallback("onComplete", () => {
      /*
       * Jump seek to Services (or later) runs heroB.seek("end") while crossing
       * cases-in — do not re-arm Our Work thumbs over SUB-SERVICES.
       * Gate on seekMode / jumpIndex only — stale servicesArmed (ex-reset)
       * previously skipped arming after settleWorkIntro → cases scroll.
       */
      if (m.seekMode || m.jumpIndex >= 2) {
        m.lock(false);
        return;
      }
      m.servicesArmed = false;
      setSideNavMode("work", true);
      m.lock(false);
    });
    m.heroB.eventCallback("onReverseComplete", () => {
      /* Soft re-anchor: scrub 1→0 restores titles + grid — never hard-snap thumbs. */
      returnToGeneralWork(() => {
        m.slider?.pause();
        m.lock(false);
      });
    });

    const thumbY = (i: number) => {
      const h = fkInners[0]?.offsetHeight ?? workThumbs[0]?.offsetHeight ?? 0;
      const gridTop = workGridH?.getBoundingClientRect().top ?? 0;
      return -(window.innerHeight / 2 - h / 2) + gridTop + (h + m.thumbGap) * i;
    };

    const workScrub = gsap
      .timeline({
        scrollTrigger: {
          id: "workTrigger",
          trigger: "#workTrigger",
          start: "0% 0%",
          end: "100% 0%",
          scrub: true,
        },
        defaults: { ease: "none" },
      })
      .to(
        fkThumbs,
        { x: 0, y: (_i, el) => thumbY(fkThumbs.indexOf(el as HTMLElement)) },
        0,
      )
      .fromTo(
        workWrap,
        { y: 0 },
        {
          y: () =>
            window.innerWidth * (window.innerWidth > 600 ? -0.15 : -0.75),
        },
        0,
      )
      /*
       * Blur cost scales with radius x area, and `.work_head` is a viewport-wide
       * headline block — 10px re-rasterized per scroll frame was the stall here.
       * 6px reads the same under the opacity fade, and the fade finishes at 70%
       * so the last third of the scrub composites a hidden layer instead.
       */
      .fromTo(
        "#work ._work_ele",
        { blur: 0, autoAlpha: 1 },
        /* 0.35 of the timeline's 0.5 default span — must stay under it so the
         * thumb / wrap tweens keep owning the full scroll range. */
        { blur: 6, autoAlpha: 0, duration: 0.35 },
        0,
      );
    workScrub.scrollTrigger?.disable(false);

    gsap
      .timeline({
        scrollTrigger: {
          id: "cases-trigger",
          trigger: "#cases-trigger",
          start: "0% 0%",
          end: "100% 0%",
          scrub: true,
        },
        defaults: { ease: "none" },
      })
      .to(
        fkThumbs,
        { x: 0, y: (_i, el) => thumbY(fkThumbs.indexOf(el as HTMLElement)) },
        0,
      )
      .fromTo(
        "#side-nav-work .side_nav_selector",
        { y: 0 },
        { y: "300%", duration: 1 },
        0,
      );

    const casesEnter = () => {
      if (!m.heroB) return;
      /*
       * Jump seek owns the destination via settleJump. Do not arm case intro,
       * swapSide, or snap HeroB — that raced settleWorkIntro when work lands
       * briefly crossed cases-in.
       */
      if (m.seekMode) return;

      markSpineSection("work");
      swapSide("side-work", "side-case-1", 1, 0.5);
      if (m.caseIntroTimer) {
        clearTimeout(m.caseIntroTimer);
        m.caseIntroTimer = undefined;
      }

      m.caseIntroTimer = setTimeout(() => introCase(1), 700 * U);

      /*
       * Resilient like servicesEnter: a stuck HeroB at progress 1 (seek/settle
       * left it there) made !canPlay early-return and never re-armed the rail.
       */
      if (!canPlay(m.heroB)) {
        m.heroB.pause();
        m.heroB.progress(1, true);
        if (m.jumpIndex < 2) {
          m.servicesArmed = false;
          document.getElementById("side-nav-work")?.classList.add("is-active");
          document
            .getElementById("side-nav-products")
            ?.classList.remove("is-active");
          setSideNavMode("work", true);
        }
        m.lock(false);
        return;
      }

      m.lock(true);
      m.heroB.timeScale(window.innerWidth <= 600 ? 1.55 : 1.2).play();
    };
    const casesReverse = () => {
      if (!m.heroB || !canReverse(m.heroB)) return;
      if (m.caseIntroTimer) {
        clearTimeout(m.caseIntroTimer);
        m.caseIntroTimer = undefined;
      }
      /* Seek: park HeroB only — returnToGeneralWork paints intro mid-chain. */
      if (m.seekMode) {
        m.heroB.timeScale(1.8).seek(0, false);
        return;
      }
      markSpineSection("work");
      /* Freeze scrub while HeroB owns the morph — re-enable after reverse. */
      pauseWorkScrub();
      ScrollTrigger.getById("cases-trigger")?.disable(false);
      swapSide("side-case-1", "side-work", -1);
      gsap.set(m.body, { background: "#C7C7C7", color: "#121212" });
      m.body.classList.add("work-scroll");
      hideCase(1);
      m.lock(true);
      m.heroB.timeScale(1.8).reverse();
    };
    et("cases-in", "in", casesEnter);
    et("cases-in", "reverse", casesReverse);
  }

  function bindSlideParallax(triggerId: string, index: number) {
    const stId = `${triggerId}-${index}`;
    killTrigger(stId);
    const cfg = m.slider?.slideConfig[index];
    const bg = workBgs[index];
    if (!cfg || !bg) return;
    gsap
      .timeline({
        scrollTrigger: {
          id: stId,
          trigger: `#${triggerId}`,
          start: "top top",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: () => m.slider?.setSlideOffset(index, cfg.offsetY),
        },
        defaults: { ease: "none" },
      })
      .fromTo(
        cfg,
        {
          offsetY: () => {
            const h = bg.offsetHeight;
            return -(window.innerHeight - h) / 2;
          },
        },
        {
          offsetY: () => {
            const h = bg.offsetHeight;
            return (window.innerHeight - h) / 2;
          },
        },
        0,
      );
  }

  function buildCases() {
    bindSlideParallax("slide1", 0);
    bindSlideParallax("slide2", 1);
    bindSlideParallax("slide3", 2);
    bindSlideParallax("slide4", 3);
    et("slide2-in", "in", () => {
      markSpineSection("work");
      m.slider?.goToSlide(1, 1);
      swapCase(1, 2);
      swapSide("side-case-1", "side-case-2", 1);
    });
    et("slide3-in", "in", () => {
      markSpineSection("work");
      m.slider?.goToSlide(2, 1);
      swapCase(2, 3);
      swapSide("side-case-2", "side-case-3", 1);
    });
    et("slide4-in", "in", () => {
      markSpineSection("work");
      handoffCase4Marquee(true);
      swapCase(3, 4);
      swapSide("side-case-3", "side-case-4", 1);
    });
    et("slide2-in", "reverse", () => {
      markSpineSection("work");
      m.slider?.goToSlide(0, 1);
      swapCase(2, 1);
      swapSide("side-case-2", "side-case-1", -1);
    });
    et("slide3-in", "reverse", () => {
      markSpineSection("work");
      m.slider?.goToSlide(1, 1);
      swapCase(3, 2);
      swapSide("side-case-3", "side-case-2", -1);
    });
    et("slide4-in", "reverse", () => {
      markSpineSection("work");
      handoffCase4Marquee(false);
      swapCase(4, 3);
      swapSide("side-case-4", "side-case-3", -1);
    });
  }

  let serviceUnbind: Array<() => void> = [];
  const logoTweens: gsap.core.Timeline[] = [];

  function buildServices() {
    serviceUnbind.forEach((fn) => fn());
    serviceUnbind = [];
    killTrigger("servicesTrigger");
    const rows = q<HTMLElement>(".services_row");
    const isMobile = window.innerWidth <= 600;

    gsap.set("#services", { autoAlpha: 0, y: 0, clearProps: "zIndex" });
    gsap.set(rows, { y: 32, autoAlpha: 0 });
    gsap.set("#services-mosaic", {
      autoAlpha: 1,
      clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
    });
    gsap.set("#services-mosaic .services_mosaic_plane", {
      blur: 0,
      scale: 2,
      clearProps: "filter",
    });
    gsap.set("#services-mosaic .services_mosaic_scrim", { autoAlpha: 0 });
    document.getElementById("services-mosaic")?.classList.remove("is-on");

    let busy = false;
    /*
     * Visual enter is servicesTL-driven (Products architecture) — ADR-015.
     * Never scrub the same props the paused TL animates, and never
     * killTweensOf / overwrite:true those targets from onEnter/enter —
     * that permanently detaches timeline members (Atlas/Orbit trap).
     */
    const clipOpen = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const clipShut = "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)";
    const svcFrom = { immediateRender: false } as const;
    const settledBlur = isMobile ? 18 : 22;

    /* Idle park — shut under Work until servicesTL opens. */
    gsap.set("#services-mosaic", {
      autoAlpha: 1,
      clipPath: clipShut,
    });
    gsap.set("#services-mosaic .services_mosaic_plane", {
      blur: 0,
      scale: 2,
    });
    gsap.set("#services-mosaic .services_mosaic_scrim", { autoAlpha: 0 });
    gsap.set([".work_marquee", ".work_bgs"], { y: 0 });

    m.servicesTl = gsap.timeline({ id: "servicesTL", paused: true });
    m.servicesTl
      .call(() => {
        m.slider?.pause();
        /* Do NOT hideProjectsCopy here — it killTweensOf case copy that this TL fades. */
      })
      .fromTo(
        ".side-case-4",
        { autoAlpha: 1 },
        { autoAlpha: 0, duration: 0.35, ease: EASE_IN, ...svcFrom },
        0,
      )
      .fromTo(
        [
          "#case-4",
          "#projects",
          ".project .static",
          ".project .static > .SplitClass",
          "#case-4 ._ele",
        ],
        { autoAlpha: 1 },
        { autoAlpha: 0, duration: 0.35, ease: EASE_IN, ...svcFrom },
        0,
      )
      /* Products-language bottom wipe — mosaic covers Work (z 6999 > 120). */
      .fromTo(
        "#services-mosaic",
        { autoAlpha: 1, clipPath: clipShut },
        {
          autoAlpha: 1,
          clipPath: clipOpen,
          duration: 1,
          ease: EASE_OUT,
          ...svcFrom,
        },
        0.1,
      )
      /* Cover-scale parallax (mirrors #productA .cover 2→1.6). */
      .fromTo(
        "#services-mosaic .services_mosaic_plane",
        { blur: 0, scale: 2 },
        {
          blur: settledBlur,
          scale: 1.08,
          duration: 1.4,
          ease: EASE_OUT,
          ...svcFrom,
        },
        0.1,
      )
      .fromTo(
        "#services-mosaic .services_mosaic_scrim",
        { autoAlpha: 0 },
        {
          autoAlpha: 0.72,
          duration: 0.85,
          ease: EASE_OUT,
          ...svcFrom,
        },
        0.25,
      )
      .call(
        () => {
          /* Incoming rails once wipe has coverage — Products pattern. */
          if (m.servicesTl?.reversed()) return;
          clearWorkRailForServices();
          gsap.set(".side_left", { autoAlpha: 1 });
          gsap.set(".side_right", { autoAlpha: 1 });
        },
        undefined,
        0.4,
      )
      .fromTo(
        "#services",
        {
          autoAlpha: 0,
          y: () => window.innerHeight * 0.08,
          zIndex: 200,
        },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: EASE_OUT,
          ...svcFrom,
        },
        0.5,
      )
      .fromTo(
        rows,
        { y: 40, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.055,
          duration: 0.75,
          ease: EASE_OUT,
          ...svcFrom,
        },
        0.55,
      )
      /*
       * Forward-only Work clear after mosaic covers the frame.
       * Do NOT tween Work planes on this TL — nothing else can overwrite
       * members. Skip on reverse so Work can restore via onReverseComplete.
       */
      .call(
        () => {
          if (m.servicesTl?.reversed()) return;
          clearWorkPlanesForServices();
        },
        undefined,
        1.0,
      );

    m.servicesTl.eventCallback("onComplete", () => {
      if (!busy) m.lock(false);
      m.servicesArmed = true;
      hideProjectsCopy({ keepMarquee: true });
      clearWorkPlanesForServices();
      clearWorkRailForServices();
      setServicesMarqueeBackdrop(true, true);
      gsap.set(rows, { y: 0, autoAlpha: 1 });
      gsap.set("#services", {
        autoAlpha: 1,
        y: 0,
        zIndex: 200,
        overwrite: "auto",
      });
    });
    m.servicesTl.eventCallback("onReverseComplete", () => {
      restoreWorkAfterServices();
      m.slider?.resume();
    });

    const paintServicesEntered = () => {
      gsap.set(".side_left", { autoAlpha: 1, overwrite: "auto" });
      gsap.set(".side_right", { autoAlpha: 1, overwrite: "auto" });
      gsap.set("#services", {
        autoAlpha: 1,
        y: 0,
        zIndex: 200,
        overwrite: "auto",
      });
      gsap.set("#services-mosaic", {
        autoAlpha: 1,
        clipPath: clipOpen,
        overwrite: "auto",
      });
      gsap.set("#services-mosaic .services_mosaic_plane", {
        blur: settledBlur,
        scale: 1.08,
        overwrite: "auto",
      });
      gsap.set("#services-mosaic .services_mosaic_scrim", {
        autoAlpha: 0.72,
        overwrite: "auto",
      });
      gsap.set(rows, { y: 0, autoAlpha: 1, overwrite: "auto" });
      clearWorkPlanesForServices();
      clearWorkRailForServices();
    };

    /*
     * Bookkeeping-only ST — keeps snapScrub("servicesTrigger", …) and
     * st.start lookups alive. Empty timeline: no props shared with servicesTL.
     */
    gsap.timeline({
      scrollTrigger: {
        id: "servicesTrigger",
        trigger: "#servicesTrigger",
        start: "0% 0%",
        end: "100% 0%",
        scrub: true,
        invalidateOnRefresh: true,
        onEnter: () => {
          markSpineSection("work");
          setCase4Bridge("off", true);
        },
        onLeaveBack: () => {
          /* Don't fight an in-flight reverse of servicesTL. */
          if (m.servicesTl?.isActive()) return;
          markSpineSection("work");
          restoreWorkAfterServices();
          m.slider?.resume();
        },
      },
      defaults: { ease: "none" },
    });

    const servicesEnter = () => {
      if (!m.servicesTl) return;
      markSpineSection("services");
      setCase4Bridge("off", true);
      /*
       * Seed from-state only — never killTweensOf / overwrite:true targets
       * that servicesTL will animate (detaches members permanently).
       */
      snapScrub("servicesTrigger", 1);

      const serviceKey = "side-service-1";
      m.serviceIndex = 0;
      m.lastServiceKey = serviceKey;
      m.setJumpIndex(2);
      m.servicesArmed = false;
      void m.preloadProductVideos();

      /*
       * Jump/seek chain: park TL only — do NOT paintServicesEntered. Mid-chain
       * paints race settleJump and leave Work/Services/Products stacked.
       */
      if (m.seekMode) {
        m.servicesTl.pause();
        m.servicesTl.progress(1, false);
        m.servicesArmed = true;
        return;
      }

      if (!canPlay(m.servicesTl)) {
        m.servicesTl.pause();
        m.servicesTl.progress(1, false);
        paintServicesEntered();
        setServicesMarqueeBackdrop(true, true);
        if (isMobile) snapSide(serviceKey);
        else swapSide(null, serviceKey, 1);
        setService(0);
        m.servicesArmed = true;
        m.lock(false);
      } else {
        /* Products pattern — hold scroll while the morph plays. */
        m.lock(true);
        m.servicesTl.play();
        if (isMobile) snapSide(serviceKey);
        else swapSide(null, serviceKey, 1);
        setService(0);
      }
    };
    let servicesReverseQueued = false;
    const runServicesReverse = () => {
      if (!m.servicesTl) return;
      servicesReverseQueued = false;
      markSpineSection("work");
      m.servicesArmed = false;
      const target = resolveWorkCase();
      /*
       * Seek path: only park scrub/TL. Never unpause smoother or hijack
       * scrollTop (fights transitionToSection), and never restoreWorkAfterServices
       * (paints case copy over the jump destination — settleJump owns paint).
       */
      if (m.seekMode) {
        m.servicesTl.pause();
        m.servicesTl.progress(0, false);
        snapScrub("servicesTrigger", 0);
        m.setJumpIndex(1);
        return;
      }
      m.lock(true);
      if (!canReverse(m.servicesTl)) {
        m.servicesTl.pause();
        m.servicesTl.progress(0, false);
        snapScrub("servicesTrigger", 0);
        restoreWorkAfterServices();
        m.slider?.resume();
        const fromKey = m.lastServiceKey?.startsWith("side-service")
          ? m.lastServiceKey
          : "side-service-1";
        if (isMobile) snapSide(`side-case-${target}`);
        else swapSide(fromKey, `side-case-${target}`, -1);
        m.setJumpIndex(1);
        return;
      }
      /*
       * Work under open mosaic BEFORE wipe shuts — otherwise reverse reveals
       * body #080808 (black) until onReverseComplete.
       */
      paintWorkPlaneUnderServices(target);
      m.servicesTl.reverse();
      const fromKey = m.lastServiceKey?.startsWith("side-service")
        ? m.lastServiceKey
        : "side-service-1";
      if (!isMobile) swapSide(fromKey, `side-case-${target}`, -1);
      m.setJumpIndex(1);
    };
    const servicesReverse = () => {
      if (!m.servicesTl) return;
      if (
        m.jumpIndex > 2 ||
        (m.productsTl &&
          (m.productsTl.isActive() || m.productsTl.progress() > 0.02))
      ) {
        servicesReverseQueued = true;
        return;
      }
      runServicesReverse();
    };
    et("services-in", "in", servicesEnter);
    et("services-in", "reverse", servicesReverse);
    /* Expose queue drain for products reverse completion. */
    (m as SiteMotion & { _drainServicesReverse?: () => void })._drainServicesReverse =
      () => {
        if (!servicesReverseQueued) return;
        /*
         * Only continue Work reverse if scroll is still at/above the services-in
         * reverse gate. If the user settled on Services after Products released,
         * clear the queue — draining would lock + wipe to Work (felt like freeze).
         */
        const rev = ScrollTrigger.getById("services-in-reverse");
        const scroll =
          typeof m.smoother?.scrollTop === "function"
            ? m.smoother.scrollTop()
            : window.scrollY;
        if (
          rev &&
          typeof rev.start === "number" &&
          scroll > rev.start + 8
        ) {
          servicesReverseQueued = false;
          return;
        }
        runServicesReverse();
      };
    /* Scrub creation can leave mid-tween props — pin hero-safe defaults. */
    if (m.caseIndex !== 4) {
      setWorkMarquee(false, true);
      gsap.set([".work_bgs", ".work_marquee"], {
        autoAlpha: 0,
        scale: 1,
        blur: 0,
      });
    }
    setServicesMarqueeBackdrop(false, true);
    let hold: ReturnType<typeof setTimeout> | null = null;
    let pulse: ReturnType<typeof setInterval> | null = null;
    const activate = (row: HTMLElement, index: number) => {
      if (document.getElementById("services")?.dataset.bento === "1") return;
      if (!m.servicesArmed) return;
      if (row.classList.contains("active") && index === m.serviceIndex) return;
      const prev = m.serviceIndex;
      if (index === prev) return;
      busy = true;
      const fromKey = `side-service-${prev + 1}`;
      const toKey = `side-service-${index + 1}`;
      m.lastServiceKey = toKey;
      setService(index);
      m.serviceIndex = index;
      if (hold) clearTimeout(hold);
      if (pulse) {
        clearInterval(pulse);
        pulse = null;
      }
      /* Don't lock ScrollSmoother on hover — that felt like getting stuck. */
      if (isMobile) {
        snapSide(toKey);
        busy = false;
        return;
      }
      swapSide(fromKey, toKey, index > prev ? 1 : -1, 0, () => {
        hold = setTimeout(() => {
          busy = false;
        }, 120);
      });
    };
    if (!(m.isTouch || window.innerWidth <= 600)) {
      rows.forEach((row, i) => {
        const handler = () => activate(row, i);
        row.addEventListener("mouseenter", handler);
        serviceUnbind.push(() => row.removeEventListener("mouseenter", handler));
      });
    }
  }

  function buildProducts() {
    const productsFrom = { immediateRender: false } as const;
    let productsReverseParallax: gsap.core.Tween | null = null;
    const killProductsReverseParallax = () => {
      productsReverseParallax?.kill();
      productsReverseParallax = null;
    };
    m.productsTl = gsap.timeline({ id: "productsTL", paused: true });
    m.productsTl
      /*
       * Explicit fromTo — never .to() after hideServicesPlane, or reverse
       * records start=0 and services/mosaic stay dead when leaving products.
       * Keep services exit short — #services sits in container_wrap (z 6999)
       * above .products_wrap, so a long fade reads as a frozen overlay.
       */
      .fromTo(
        ".services_row",
        { y: 0, autoAlpha: 1 },
        {
          y: -24,
          autoAlpha: 0,
          duration: 0.32,
          ease: EASE_IN,
          stagger: 0.025,
          ...productsFrom,
        },
        0,
      )
      .fromTo(
        "#services",
        { autoAlpha: 1, y: 0, zIndex: 200 },
        {
          autoAlpha: 0,
          y: 0,
          duration: 0.28,
          ease: EASE_IN,
          clearProps: "zIndex",
          ...productsFrom,
        },
        0,
      )
      /*
       * Mosaic opacity stays off this TL. Forward hard-clears below; reverse
       * pre-paints + runs a cover-scale parallax (scale from→to) so Products
       * wrap never closes onto body black and the handoff reads as a morph.
       * Do not fromTo mosaic scale here — that fought the reverse parallax.
       */
      .call(
        () => {
          if (m.productsTl?.reversed()) return;
          gsap.set("#services", {
            autoAlpha: 0,
            clearProps: "zIndex",
            overwrite: "auto",
          });
          gsap.set(".services_row", { autoAlpha: 0, overwrite: "auto" });
          gsap.set("#services-mosaic", { autoAlpha: 0, overwrite: "auto" });
          gsap.set("#services-mosaic .services_mosaic_scrim", {
            autoAlpha: 0,
            overwrite: "auto",
          });
        },
        undefined,
        0.3,
      )
      .call(() => {
        /* Wait for first product frame when possible (bounded in playProductVideos). */
        void m.playProductVideos("play");
      }, undefined, 0.28)
      .set(["#products", "#productA"], { autoAlpha: 1 }, 0.28)
      .call(() => setSideNavMode("none", true), undefined, 0)
      .set(
        "#side-nav-work",
        { autoAlpha: 0, pointerEvents: "none", overwrite: true },
        0,
      )
      .fromTo(
        ".products_wrap",
        { clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)" },
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          duration: 1,
          ease: EASE_OUT,
          ...productsFrom,
        },
        0.12,
      )
      .call(
        () => {
          /* Incoming rail only once destination plane has coverage. */
          setSideNavMode("products", true);
          gsap.set(".side_left", { autoAlpha: 1 });
          snapSide(m.lastProductKey || "side-product-1");
        },
        undefined,
        0.35,
      )
      .fromTo(
        "#productA .product_video",
        { scale: 2 },
        { scale: 1.6, duration: 1.5, ease: EASE_OUT, ...productsFrom },
        1,
      )
      .fromTo(
        "#side-nav-products",
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 1,
          ease: EASE_OUT,
          onStart: () => {
            document
              .getElementById("side-nav-products")
              ?.classList.add("is-active");
            document
              .getElementById("side-nav-work")
              ?.classList.remove("is-active");
          },
          ...productsFrom,
        },
        0.35,
      )
      .fromTo(
        "#side-nav-products .side_nav_item",
        { y: "100%", autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1,
          duration: 1,
          ease: EASE_OUT,
          ...productsFrom,
        },
        0.35,
      );
    /*
     * No terminal .set(mosaic/services → 0) — that re-applied at reverse start
     * and wiped paintServicesMosaicSettled, so the wrap closed onto black and
     * Services only snapped in onReverseComplete. onComplete still hard-clears.
     */
    m.productsTl.eventCallback("onComplete", () => {
      killProductsReverseParallax();
      setServicesMarqueeBackdrop(false, true);
      clearWorkPlanesForServices();
      gsap.set(["#services", "#services-mosaic"], {
        autoAlpha: 0,
        overwrite: "auto",
      });
      gsap.set("#services", { clearProps: "zIndex", overwrite: "auto" });
      gsap.set(".services_row", { autoAlpha: 0, overwrite: "auto" });
      /*
       * Fast scroll / settleProductsTimeline can finish products after story
       * already claimed the rails (jumpIndex ≥ 4). Never re-arm Atlas/Orbit.
       */
      if (
        m.jumpIndex >= 4 ||
        m.sideKey === "side-story" ||
        m.sideKey === "side-contact" ||
        (m.storyTl?.progress() ?? 0) > 0.01 ||
        (m.contactTl?.progress() ?? 0) > 0.01
      ) {
        setSideNavMode("none", true);
        m.lock(false);
        return;
      }
      setSideNavMode("products", true);
      gsap.set(".side_left", { autoAlpha: 1 });
      snapSide(m.lastProductKey || "side-product-1");
      syncProductsScrub();
      m.lock(false);
    });
    m.productsTl.eventCallback("onReverseComplete", () => {
      killProductsReverseParallax();
      markSpineSection("services");
      paintServicesMosaicSettled();
      setServicesMarqueeBackdrop(true, true);
      clearWorkPlanesForServices();
      setSideNavMode("none", true);
      gsap.set("#services", { autoAlpha: 1, y: 0, zIndex: 200 });
      gsap.set(".services_row", { y: 0, autoAlpha: 1, overwrite: "auto" });
      gsap.set(["#products", "#productA", "#productB"], {
        autoAlpha: 0,
        overwrite: "auto",
      });
      const toKey = m.lastServiceKey || "side-service-1";
      snapSide(toKey);
      m.lock(false);
      m.servicesArmed = true;
      (
        m as SiteMotion & { _drainServicesReverse?: () => void }
      )._drainServicesReverse?.();
    });

    /** Atlas→Orbit scrub lives on buildSpine scope — heal on each products build. */
    buildProductsScrub();

    et("product-2", "in", () => {
      m.lastProductKey = "side-product-2";
      gsap.set(".side_left", { autoAlpha: 1 });
      if (m.seekMode) snapSide("side-product-2");
      else
        swapSide("side-product-1", "side-product-2", 1, 0, () =>
          snapSide("side-product-2"),
        );
    });
    et("product-2", "reverse", () => {
      m.lastProductKey = "side-product-1";
      gsap.set(".side_left", { autoAlpha: 1 });
      if (m.seekMode) snapSide("side-product-1");
      else
        swapSide("side-product-2", "side-product-1", -1, 0, () =>
          snapSide("side-product-1"),
        );
    });

    const paintServicesFromProducts = () => {
      killProductsReverseParallax();
      markSpineSection("services");
      paintServicesMosaicSettled();
      setServicesMarqueeBackdrop(true, true);
      gsap.set("#services", {
        autoAlpha: 1,
        y: 0,
        zIndex: 200,
        overwrite: "auto",
      });
      gsap.set(".services_row", { y: 0, autoAlpha: 1, overwrite: "auto" });
      gsap.set(["#products", "#productA", "#productB"], {
        autoAlpha: 0,
        overwrite: "auto",
      });
      gsap.set(".products_wrap", {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
        overwrite: "auto",
      });
      setSideNavMode("none", true);
      snapSide(m.lastServiceKey || "side-service-1");
      m.servicesArmed = true;
    };

    const productsEnter = () => {
      if (!m.productsTl) return;
      killProductsReverseParallax();
      markSpineSection("products");
      hideProjectsCopy();
      clearWorkPlanesForServices();
      setSideNavMode("none", true);
      m.lastProductKey = "side-product-1";
      /* Rails arm mid-timeline once products_wrap has coverage — not before. */
      gsap.set([".side_left", ".side_right"], { autoAlpha: 1 });
      snapScrub("servicesTrigger", 1);
      /*
       * Do NOT hideServicesPlane() here — that zeros mosaic/services before
       * productsTl records from-values, so reverse leaves planes stuck shut
       * and content freezes over the wrong section.
       */
      gsap.set("#services", {
        autoAlpha: 1,
        y: 0,
        zIndex: 200,
        overwrite: "auto",
      });
      gsap.set("#services-mosaic", {
        autoAlpha: 1,
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        overwrite: "auto",
      });
      gsap.set(".services_row", { y: 0, autoAlpha: 1, overwrite: "auto" });
      /* Reset marks so fromTo can rise them (story reverse / crash left them mid-state). */
      gsap.set("#side-nav-products .side_nav_item", {
        y: "100%",
        autoAlpha: 0,
      });
      /*
       * Never early-return on canPlay — stuck progress 1 left enter as a no-op
       * while scroll already advanced (freeze / wrong stack).
       * Snap path must NOT leave lock(true) — onComplete already unlocked, and
       * a second lock with no play would freeze ScrollSmoother permanently.
       * Seek: park only — settleJump paints the products destination.
       */
      if (m.seekMode) {
        m.productsTl.pause();
        m.productsTl.timeScale(1);
        m.productsTl.progress(1, false);
        m.servicesArmed = false;
        m.setJumpIndex(3);
        return;
      }
      if (!canPlay(m.productsTl)) {
        m.productsTl.pause();
        m.productsTl.timeScale(1);
        m.productsTl.progress(1, false);
        paintProductsEntered();
        m.lock(false);
      } else {
        /* Fresh scrub so productsTl cover tweens cannot leave ST members dead. */
        buildProductsScrub();
        /*
         * Restore forward rate only immediately before play: a positive
         * timeScale on a still-reversed timeline flips it forward on the spot.
         */
        m.productsTl.timeScale(1);
        m.productsTl.play();
        m.lock(true);
      }
      m.servicesArmed = false;
      m.setJumpIndex(3);
    };
    const productsReverse = () => {
      if (!m.productsTl) return;
      const fromKey = m.lastProductKey || "side-product-1";
      const toKey = m.lastServiceKey || "side-service-1";
      /* Outgoing product rail fades with reverse — incoming service after release. */
      setSideNavMode("none", true);
      snapScrub("productsTrigger", 0);
      /*
       * Zoomed splash under Products wrap BEFORE reverse — mosaic was hard-
       * cleared at TL end; without this, wrap closes onto body black. Parallax
       * scale eases down while the wrap clip closes.
       * Seek: park only — no paintServicesFromProducts (settleJump owns paint).
       */
      if (m.seekMode) {
        killProductsReverseParallax();
        m.productsTl.pause();
        m.productsTl.timeScale(1);
        m.productsTl.progress(0, false);
        void m.playProductVideos("pause");
        m.setJumpIndex(2);
        return;
      }
      killProductsReverseParallax();
      paintServicesMosaicSettled({ parallax: true });
      /*
       * Never early-return on canReverse — stuck progress 0 left leave as a
       * no-op with products still painted over services.
       */
      if (!canReverse(m.productsTl)) {
        m.productsTl.pause();
        m.productsTl.timeScale(1);
        m.productsTl.progress(0, false);
        paintServicesFromProducts();
        void m.playProductVideos("pause");
        m.lock(false);
        m.setJumpIndex(2);
        (
          m as SiteMotion & { _drainServicesReverse?: () => void }
        )._drainServicesReverse?.();
        return;
      }
      {
        /*
         * Enter is a reveal worth ~2.5s; leaving back to Services is a dismissal
         * but must still read as a morph (wrap close + mosaic parallax).
         */
        m.productsTl.timeScale(PRODUCTS_REVERSE_SCALE);
        const revDur = m.productsTl.duration() / PRODUCTS_REVERSE_SCALE;
        productsReverseParallax = gsap.fromTo(
          "#services-mosaic .services_mosaic_plane",
          {
            scale: PRODUCTS_REVERSE_MOSAIC_FROM,
            blur: window.innerWidth <= 600 ? 20 : 26,
          },
          {
            scale: PRODUCTS_REVERSE_MOSAIC_TO,
            blur: window.innerWidth <= 600 ? 18 : 22,
            duration: revDur,
            ease: EASE_OUT,
            overwrite: "auto",
            onComplete: () => {
              productsReverseParallax = null;
            },
          },
        );
        m.productsTl.reverse();
      }
      void m.playProductVideos("pause");
      /* Service side copy after reverse completes (onReverseComplete). */
      void fromKey;
      void toKey;
      m.lock(true);
      m.setJumpIndex(2);
    };
    et("products-in", "in", productsEnter);
    et("products-in", "reverse", productsReverse);
  }

  function logoLoops() {
    logoTweens.forEach((t) => t.kill());
    logoTweens.length = 0;
    document.querySelectorAll(".product_logos_grid").forEach((grid) => {
      grid.querySelectorAll(".product_logos_col").forEach((col) => {
        const a = col.querySelector(".logo_a");
        const b = col.querySelector(".logo_b");
        if (!a || !b) return;
        gsap.set(a, { autoAlpha: 1 });
        gsap.set(b, { autoAlpha: 0 });
        const repeatDelay = gsap.utils.random(2, 3, 0.1);
        const delay = gsap.utils.random(1, 2, 0.1);
        const loop = gsap
          .timeline({ repeat: -1, repeatDelay, delay, paused: true })
          .to(a, { duration: 1, autoAlpha: 0, ease: EASE_IN })
          .to(b, { duration: 1, autoAlpha: 1, ease: EASE_OUT }, ">+0.5")
          .to({}, { duration: delay / 2 })
          .to(b, { duration: 1, autoAlpha: 0, ease: EASE_IN })
          .to(a, { duration: 1, autoAlpha: 1, ease: EASE_OUT }, ">+0.5");
        logoTweens.push(loop);
        ScrollTrigger.create({
          trigger: grid,
          start: "top 85%",
          end: "bottom 15%",
          onEnter: () => loop.play(),
          onEnterBack: () => loop.play(),
          onLeave: () => loop.pause(),
          onLeaveBack: () => loop.pause(),
        });
      });
    });
  }

  function buildStory() {
    killTrigger("storyTrigger");
    m.storyTl = gsap.timeline({ id: "storyTL", paused: true });
    /*
     * `.products_wrap` and `#about` are NOT on storyTl. Position-based
     * scheduling cannot satisfy both directions — a high #about fade left
     * Story painted over an open Products plane for ~1.3s on reverse, and a
     * wrap fromTo re-rendered closed after reverseComplete (flash→black).
     * Drive both with standalone tweens killed on the opposite direction.
     * Do NOT .call(setSideNavMode("none")) at t=0 — callbacks fire both
     * ways and would kill the products rail that storyReverse just armed.
     */
    const WRAP_OPEN = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const WRAP_CLOSED_TOP = "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)";
    let productsWrapStoryTween: gsap.core.Tween | null = null;
    let aboutFadeTween: gsap.core.Tween | null = null;
    /** Suppress spurious story-in onEnter right after reverse (wrap flash→black). */
    let storyEnterGuardUntil = 0;
    const killProductsWrapStoryTween = () => {
      productsWrapStoryTween?.kill();
      productsWrapStoryTween = null;
    };
    const killAboutFadeTween = () => {
      aboutFadeTween?.kill();
      aboutFadeTween = null;
    };
    m.storyTl
      .fromTo(
        ".about_head h1.desktop span",
        {
          autoAlpha: 0,
          y: () => 0.25 * window.innerHeight,
          blur: 10,
          rotateX: 90,
        },
        {
          autoAlpha: 1,
          y: 0,
          blur: 0,
          rotateX: 0,
          stagger: 0.1,
          duration: 1,
          ease: EASE_OUT,
        },
        1,
      )
      .fromTo(
        ".about_head h1.mobile span",
        {
          autoAlpha: 0,
          y: () => 0.25 * window.innerHeight,
          blur: 10,
          rotateX: 90,
        },
        {
          autoAlpha: 1,
          y: 0,
          blur: 0,
          rotateX: 0,
          stagger: 0.1,
          duration: 1,
          ease: EASE_OUT,
          onStart: () => {
            document
              .querySelectorAll(".facts_block .odometer")
              .forEach((el) => {
                const node = el as HTMLElement;
                node.innerHTML = "";
                node.innerHTML = node.dataset.counter || "";
              });
            window.dispatchEvent(new Event("xattir:story-facts"));
          },
        },
        1,
      )
      .fromTo(
        ".about_head ._ele",
        {
          autoAlpha: 0,
          y: () => 0.25 * window.innerHeight,
          blur: 10,
          rotateX: 90,
        },
        {
          autoAlpha: 1,
          y: 0,
          blur: 0,
          rotateX: 0,
          stagger: 0.1,
          duration: 1,
          ease: EASE_OUT,
        },
        1.5,
      );
    m.storyTl.eventCallback("onComplete", () => {
      m.playProductVideos("pause");
      /* Re-assert — productsTl.onComplete / scrub can re-arm Atlas/Orbit marks. */
      setSideNavMode("none", true);
      m.lock(false);
    });
    m.storyTl.eventCallback("onReverseComplete", () => {
      m.storyTl?.pause();
      m.storyTl?.reversed(false);
      m.storyTl?.progress(0, true);
      storyEnterGuardUntil = performance.now() + 2800;
      if (m.body.dataset.spineSection === "products") {
        /*
         * Leave the open-wipe tween alone (killing it can revert clip to the
         * closed "from"). Pin open; scrub was already rebuilt in storyReverse.
         */
        productsWrapStoryTween = null;
        aboutFadeTween = null;
        gsap.set("#about", { autoAlpha: 0, overwrite: "auto" });
        gsap.set("#products", { autoAlpha: 1, overwrite: "auto" });
        gsap.set(["#productA", "#productB"], {
          autoAlpha: 1,
          overwrite: "auto",
        });
        gsap.set("#products video", { autoAlpha: 1, overwrite: "auto" });
        gsap.set(".products_wrap", {
          clipPath: WRAP_OPEN,
          overwrite: "auto",
        });
        setSideNavMode("products", true);
        buildProductsScrub();
        poseProductsScrub();
        snapSide(m.lastProductKey || "side-product-1");
        m.playProductVideos("play");
      }
      m.lock(false);
      gsap.set(".contact_cover", { autoAlpha: 0 });
    });
    gsap
      .timeline({
        scrollTrigger: {
          id: "storyTrigger",
          trigger: "#storyTrigger",
          start: "0% 0%",
          end: "90% 0%",
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
        defaults: { ease: "none" },
      })
      .set(".contact_cover", { autoAlpha: 1, overwrite: "auto" }, 0)
      .fromTo(
        ".contact_cover",
        { clipPath: () => contactClipLine() },
        { clipPath: () => contactClipPeek(), duration: 0.9 },
        0.1,
      )
      .fromTo(
        ".contact_cover > i",
        { scale: 1 },
        { scale: 0.6, duration: 1 },
        0,
      )
      .fromTo(
        aboutHeads[0],
        { y: 0, autoAlpha: 1, blur: 0 },
        {
          y: () => -0.25 * window.innerHeight,
          autoAlpha: 0.3,
          blur: 10,
          duration: 1,
        },
        0,
      )
      .fromTo(
        aboutHeads[1],
        { y: 0, autoAlpha: 1, blur: 0 },
        {
          y: () => 0.25 * window.innerHeight,
          autoAlpha: 0.3,
          blur: 10,
          duration: 1,
        },
        0,
      );
    /** Story enter/reverse — wrap wipe is a standalone tween (not on storyTl). */
    const storyEnter = () => {
      if (!m.storyTl) return;
      if (performance.now() < storyEnterGuardUntil) return;
      /*
       * ScrollTrigger refresh / smoother bounce can re-fire story-in onEnter
       * while scroll is still above the 50% gate — that called hideProductsPlane
       * and slammed the wrap shut right after reverse opened it.
       */
      const storyInSt = ScrollTrigger.getById("story-in-in");
      if (
        storyInSt &&
        m.body.dataset.spineSection === "products" &&
        storyInSt.scroll() < storyInSt.start
      ) {
        return;
      }
      hideProjectsCopy();
      hideServicesPlane();
      clearGlobalWorkLayers();
      /* Claim story before settle so productsTl.onComplete cannot re-arm Atlas/Orbit. */
      m.setJumpIndex(4);
      markSpineSection("story");
      /* Finish products enter if mid-flight — storyTL then clips the wrap away. */
      settleProductsTimeline();
      /*
       * Hard-hide products plane + side-nav. Under RTL, SideRight (facts +
       * #side-nav-products) sits on the visual left — ghosts are obvious.
       * CSS :not(.is-active) also blocks productsTrigger scrub punch-through.
       */
      hideProductsPlane();
      setSideNavMode("none", true);
      gsap.set([".side_left", ".side_right"], { autoAlpha: 1 });
      /* Stuck at progress 1 (seek / prior settle): snap paint instead of early-return. */
      if (m.seekMode) {
        killProductsWrapStoryTween();
        killAboutFadeTween();
        m.storyTl.pause();
        m.storyTl.progress(1, true);
        /* Minimal park — settleJump("story") asserts full destination matrix. */
        return;
      }
      if (!canPlay(m.storyTl)) {
        killProductsWrapStoryTween();
        killAboutFadeTween();
        gsap.set(".products_wrap", { clipPath: WRAP_CLOSED_TOP, overwrite: true });
        gsap.set("#about", { autoAlpha: 1, overwrite: "auto" });
        m.storyTl.pause();
        m.storyTl.progress(1, true);
        snapSide("side-story");
        window.dispatchEvent(new Event("xattir:story-facts"));
        m.lock(false);
      } else {
        /* Animated top-wipe (was storyTl fromTo — kept off the TL; see above). */
        killProductsWrapStoryTween();
        killAboutFadeTween();
        gsap.set(".products_wrap", { clipPath: WRAP_OPEN, overwrite: true });
        productsWrapStoryTween = gsap.to(".products_wrap", {
          clipPath: WRAP_CLOSED_TOP,
          duration: 1.5,
          ease: EASE_IO,
        });
        aboutFadeTween = gsap.to("#about", {
          autoAlpha: 1,
          duration: 0.45,
          ease: EASE_OUT,
          overwrite: "auto",
        });
        m.storyTl.play();
        swapSide(m.lastProductKey, "side-story", 1, 0, () => {
          snapSide("side-story");
          window.dispatchEvent(new Event("xattir:story-facts"));
        });
        m.lock(true);
      }
    };
    const storyReverse = () => {
      if (!m.storyTl) return;
      /*
       * Seek: park Story only — do not paintProductsEntered (settleJump owns
       * the real destination; mid-chain paint left Products over Work/Services).
       */
      if (m.seekMode) {
        killProductsWrapStoryTween();
        killAboutFadeTween();
        m.storyTl.pause();
        m.storyTl.progress(0, true);
        gsap.set("#about", { autoAlpha: 0, overwrite: "auto" });
        m.setJumpIndex(3);
        return;
      }
      /*
       * Snap fallback — same resilience as productsEnter. Early-return on
       * !canReverse left Products unpainted (black plane, rail only).
       */
      if (!canReverse(m.storyTl)) {
        markSpineSection("products");
        killProductsWrapStoryTween();
        killAboutFadeTween();
        gsap.set("#about", { autoAlpha: 0, overwrite: "auto" });
        paintProductsEntered();
        m.playProductVideos("play");
        snapSide(m.lastProductKey || "side-product-1");
        gsap.set(".contact_cover", {
          autoAlpha: 0,
          clipPath: contactClipLine(),
          overwrite: "auto",
        });
        m.setJumpIndex(3);
        m.lock(false);
        return;
      }
      markSpineSection("products");
      storyEnterGuardUntil = performance.now() + 2800;
      gsap.set("#products", { autoAlpha: 1, overwrite: "auto" });
      gsap.set(["#productA", "#productB"], {
        autoAlpha: 1,
        overwrite: "auto",
      });
      gsap.set("#products video", { autoAlpha: 1, overwrite: "auto" });
      buildProductsScrub();
      poseProductsScrub();
      /* Crossfade: Story fades out while the products wrap wipes open. */
      killProductsWrapStoryTween();
      killAboutFadeTween();
      aboutFadeTween = gsap.to("#about", {
        autoAlpha: 0,
        duration: 0.45,
        ease: EASE_IN,
        overwrite: "auto",
      });
      productsWrapStoryTween = gsap.to(".products_wrap", {
        clipPath: WRAP_OPEN,
        duration: 1.1,
        ease: EASE_IO,
        overwrite: true,
      });
      m.storyTl.reverse();
      m.playProductVideos("play");
      m.lock(true);
      /*
       * Start side swap immediately (delay 0) — delay 1 left Story facts
       * painted beside Atlas/Orbit for a full second. Arm the products rail
       * after #about has begun fading so rails don't lead.
       */
      swapSide("side-story", m.lastProductKey || "side-product-1", -1, 0);
      gsap.delayedCall(0.55, () => {
        if (m.body.dataset.spineSection !== "products") return;
        if (m.jumpIndex !== 3) return;
        gsap.set("#side-nav-products .side_nav_item", {
          y: 0,
          autoAlpha: 1,
          clearProps: "transform",
        });
        setSideNavMode("products", true);
        buildProductsScrub();
        poseProductsScrub();
        m.playProductVideos("play");
      });
      gsap.set(".contact_cover", {
        autoAlpha: 0,
        clipPath: contactClipLine(),
        overwrite: "auto",
      });
      m.setJumpIndex(3);
    };
    et("story-in", "in", storyEnter);
    et("story-in", "reverse", storyReverse);
  }

  function buildContact() {
    m.contactTl = gsap.timeline({ id: "contactTL", paused: true });
    m.contactTl
      .fromTo(
        ".contact_cover",
        { clipPath: () => contactClipPeek() },
        { clipPath: CONTACT_CLIP_FULL, duration: U, ease: EASE_IO },
        0,
      )
      .fromTo(
        ".contact_cover > i",
        { scale: 0.6, blur: 0 },
        { scale: 1, blur: 30, duration: U, ease: EASE_IO },
        0,
      );
    if (aboutHeads[0]) {
      m.contactTl.fromTo(
        aboutHeads[0],
        {
          autoAlpha: 0.3,
          y: () => -0.25 * window.innerHeight,
          blur: 10,
        },
        {
          autoAlpha: 0,
          y: () => -0.45 * window.innerHeight,
          duration: U,
          ease: EASE_IO,
        },
        0,
      );
    }
    if (aboutHeads[1]) {
      m.contactTl.fromTo(
        aboutHeads[1],
        {
          autoAlpha: 0.3,
          y: () => 0.25 * window.innerHeight,
          blur: 10,
        },
        {
          autoAlpha: 0,
          y: () => 0.45 * window.innerHeight,
          duration: U,
          ease: EASE_IO,
        },
        0,
      );
    }
    m.contactTl
      .set("#contact", { autoAlpha: 1 }, 0.5)
      .fromTo(
        "#contact ._ele",
        { y: () => 0.1 * window.innerHeight, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, stagger: 0.05, duration: 1, ease: EASE_OUT },
        0.5,
      );
    m.contactTl.eventCallback("onComplete", () => {
      m.lock(false);
    });
    m.contactTl.eventCallback("onReverseComplete", () => {
      m.lock(false);
      gsap.set("#contact", { autoAlpha: 0 });
      restoreContactPeekFromStory();
      setStoryScrubActive(true);
    });
    const contactEnter = () => {
      if (!m.contactTl) return;
      hideProjectsCopy();
      hideProductsPlane();
      clearGlobalWorkLayers();
      /* Story may still be mid-play after a flick — settle it before contact cover. */
      if (m.storyTl && m.storyTl.progress() < 0.99) {
        m.storyTl.pause();
        m.storyTl.progress(1, true);
      }
      setStoryScrubActive(false);
      primeContactHandoff();
      /* Keep prior landing planes from eating form clicks (transparent hit targets). */
      gsap.set(
        ["#hero", "#work", "#projects", "#services", "#products", "#about"],
        { pointerEvents: "none", overwrite: "auto" },
      );
      document.getElementById("contact")?.classList.add("is-active");
      m.body.classList.add("contact-active");
      markSpineSection("contact");
      gsap.set("#contact", { pointerEvents: "auto", overwrite: "auto" });
      gsap.set(".contact_cover", { pointerEvents: "none", overwrite: "auto" });
      /* Stuck at progress 1: snap paint instead of early-return (jump / settle). */
      if (m.seekMode) {
        m.contactTl.pause();
        m.contactTl.progress(1, true);
        m.setJumpIndex(5);
        return;
      }
      if (!canPlay(m.contactTl)) {
        m.contactTl.pause();
        m.contactTl.progress(1, true);
        m.lock(false);
      } else {
        m.contactTl.restart();
        m.lock(true);
      }
      swapSide("side-story", "side-contact", 1);
      m.setJumpIndex(5);
    };
    const contactReverse = () => {
      if (!m.contactTl || !canReverse(m.contactTl)) return;
      document.getElementById("contact")?.classList.remove("is-active");
      m.body.classList.remove("contact-active");
      gsap.set("#contact", { pointerEvents: "none", overwrite: "auto" });
      if (m.seekMode) {
        m.contactTl.progress(0);
        gsap.set("#contact", { autoAlpha: 0 });
        /* settleJump("story") asserts line clip — do not peek mid-chain. */
        m.lock(false);
      } else {
        m.contactTl.reverse();
        m.lock(true);
      }
      if (!m.seekMode) {
        swapSide("side-contact", "side-story", -1);
      }
      markSpineSection("story");
      m.setJumpIndex(4);
    };
    et("contact-in", "in", contactEnter);
    et("contact-in", "reverse", contactReverse);
  }

  function buildProgress() {
    gsap
      .timeline({
        scrollTrigger: {
          id: "progressTrigger",
          trigger: ".trigger_section",
          start: "0% 0%",
          end: "100% 100%",
          scrub: true,
        },
        defaults: { ease: "none" },
      })
      .fromTo(
        ".progress_line > i",
        { scaleY: 0 },
        { scaleY: 1, duration: 1 },
        0,
      );
  }

  function placeThumbs() {
    /* Inline hero thumbs removed — A/B park on work slots (hidden until heroA). */
    placeThumb(thumbA, workThumbs[0] ?? null);
    placeThumb(thumbB, workThumbs[2] ?? null);
    placeThumb(thumbC, workThumbs[1] ?? null);
    placeThumb(thumbD, workThumbs[3] ?? null);
  }

  function pauseWorkScrub() {
    ScrollTrigger.getById("workTrigger")?.disable(false);
  }

  /** Clear scrub offsets only when leaving work after heroA completed — not on hero enter. */
  function prepareHeroReverse() {
    pauseWorkScrub();
    setWorkMarquee(false, true);
    gsap.set([".work_bgs", ".work_marquee"], {
      autoAlpha: 0,
      scale: 1,
      blur: 0,
    });
    const st = ScrollTrigger.getById("workTrigger");
    const scrubTl = st?.animation as gsap.core.Timeline | undefined;
    if (
      !m.heroA ||
      m.heroA.progress() < 0.99 ||
      !scrubTl ||
      scrubTl.progress() <= 0
    )
      return;
    scrubTl.progress(0);
    if (workWrap) gsap.set(workWrap, { y: 0 });
    gsap.set("#work ._work_ele", { blur: 0, autoAlpha: 1 });
    gsap.set(fkThumbs, { x: 0, y: 0, overwrite: "auto" });
    snapThumbsToWork();
  }

  function releaseWorkScrub() {
    const st = ScrollTrigger.getById("workTrigger");
    if (!st) return;
    st.animation?.invalidate();
    st.enable();
    st.refresh();
  }

  function snapThumbsToWork() {
    const slots: Array<[HTMLElement | null, HTMLElement | null, HTMLElement | null]> = [
      [thumbA, workThumbs[0] ?? null, fkInners[0] ?? null],
      [thumbC, workThumbs[1] ?? null, fkInners[1] ?? null],
      [thumbB, workThumbs[2] ?? null, fkInners[2] ?? null],
      [thumbD, workThumbs[3] ?? null, fkInners[3] ?? null],
    ];
    for (const [thumb, hole, inner] of slots) {
      if (!thumb || !hole) continue;
      placeThumb(thumb, hole);
      gsap.set(thumb, { clipPath: clipTo(thumb, inner ?? hole) });
    }
  }

  /**
   * Cases → general Our Work.
   * HeroB reverse leaves thumbs at scrub-end; scrolling workTrigger 1→0
   * restores titles + grid via scrub (no hard snap).
   */
  function returnToGeneralWork(onDone?: () => void) {
    const workSt = ScrollTrigger.getById("workTrigger");
    const workScrub = workSt?.animation as gsap.core.Timeline | undefined;
    const casesSt = ScrollTrigger.getById("cases-trigger");

    gsap.set("#work", { autoAlpha: 1 });
    gsap.set(".work_bgs", { autoAlpha: 0 });
    gsap.set(".work_bgs .overlay", { autoAlpha: 0 });
    gsap.set(["#side-nav-work", "#side-nav-work .side_nav_item"], {
      autoAlpha: 0,
    });
    setSideNavMode("none", true);
    gsap.set(fkThumbs, { autoAlpha: 1, overwrite: "auto" });
    gsap.set(m.body, { background: "#C7C7C7", color: "#121212" });
    m.body.classList.add("work-scroll");
    m.slider?.pause();

    /* Keep scrub at end until scroll drives it back — progress(0) here snaps. */
    if (workScrub && workScrub.progress() < 0.99) {
      workScrub.progress(1);
    }

    if (!m.smoother || !workSt) {
      gsap.set("#work ._work_ele", {
        blur: 0,
        autoAlpha: 1,
        clearProps: "filter",
      });
      if (workWrap) gsap.set(workWrap, { y: 0 });
      snapThumbsToWork();
      workScrub?.progress(0);
      releaseWorkScrub();
      casesSt?.enable();
      onDone?.();
      return;
    }

    workSt.enable();
    casesSt?.disable(false);

    const top = Math.max(
      0,
      (typeof workSt.start === "number" ? workSt.start : 0) - 2,
    );

    m.smoother.paused(false);
    gsap.to(m.smoother, {
      scrollTop: top,
      duration: 1,
      ease: EASE_IO,
      overwrite: "auto",
      onComplete: () => {
        /* Scrub should already be ~0; force titles/wrap in case smoother skipped updates. */
        workScrub?.progress(0);
        if (workWrap) gsap.set(workWrap, { y: 0 });
        gsap.set("#work ._work_ele", {
          blur: 0,
          autoAlpha: 1,
          clearProps: "filter",
        });
        /* Align clips only — do not killTweens/hard-set x,y (that was the snap). */
        const slots: Array<
          [HTMLElement | null, HTMLElement | null, HTMLElement | null]
        > = [
          [thumbA, workThumbs[0] ?? null, fkInners[0] ?? null],
          [thumbC, workThumbs[1] ?? null, fkInners[1] ?? null],
          [thumbB, workThumbs[2] ?? null, fkInners[2] ?? null],
          [thumbD, workThumbs[3] ?? null, fkInners[3] ?? null],
        ];
        for (const [thumb, hole, inner] of slots) {
          if (!thumb || !hole) continue;
          gsap.set(thumb, { clipPath: clipTo(thumb, inner ?? hole) });
        }
        /* Enable without invalidate — invalidate re-snaps from live transforms. */
        workSt.enable();
        casesSt?.enable();
        onDone?.();
      },
    });
  }

  function rebuild() {
    reset();
    buildHeroA();
    buildHeroB();
    buildCases();
    buildServices();
    buildProducts();
    buildStory();
    buildContact();
    buildProgress();
    m.setJumpIndex(1);
  }

  function bindOnce() {
    fkInners.forEach((el) => {
      if (workThumbs[0]) m.syncSize(el, workThumbs[0]);
    });
    if (workGridH && workThumbs[0]) m.syncSize(workGridH, workThumbs[0]);
    /* Latest-projects thumbs are visual only — no click→case seek. */
  }

  return {
    reset,
    rebuildSplits,
    rebuild,
    logoLoops,
    swapCase,
    hideProjectsCopy,
    settleJump,
    bindOnce,
    placeThumbs,
    buildHeroA,
    buildHeroB,
    buildCases,
    buildServices,
    buildProducts,
    buildStory,
    buildContact,
    buildProgress,
  };
}

export type SpineApi = ReturnType<typeof buildSpine>;
